import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import * as Icons from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ProspectNotifyPanel } from "./ProspectNotifyPanel";
import { CampEmailCopy } from "./CampEmailCopy";
import { OrderHistory } from "./OrderHistory";
import { SupportAdmin } from "./support/SupportAdmin";
import { EventsAdmin } from "./EventsAdmin";
import { WaiverReport } from "./WaiverReport";
import { LateFeeReport } from "./LateFeeReport";
import { WaiveDialog } from "./WaiveDialog";
import { ExplorerProfileForm } from "./ExplorerProfileForm";
import { UnbookedVisitsAlert } from "./UnbookedVisitsAlert";
import { LinkParentDialog } from "./LinkParentDialog";
import { InviteParentDialog } from "./InviteParentDialog";
import { RecentlyDeleted } from "./RecentlyDeleted";
import { LateFeeDialog } from "@/components/LateFeeDialog";
import { DaytimeAdmin } from "./daytime/DaytimeAdmin";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const RESET_OPTS = [
  ["explorers", "Explorers (children profiles)"],
  ["parents", "Parent accounts"],
  ["bookings", "Bookings & waitlists"],
  ["sales", "Sales, orders & wallet balances"],
  ["credits", "Adventure credits"],
  ["memberships", "Gold memberships"],
  ["parties", "Party & school requests"],
  ["mailing_list", "Mailing list subscribers"],
];

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-navy text-white min-h-16 py-2 flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-10 w-10 object-contain" alt="logo" /><div><p className="font-display font-extrabold text-sm">ADMIN CONSOLE</p><p className="text-[10px] text-turquoise tracking-widest">ADVENTURE ROCK</p></div></div>
        <div className="flex flex-wrap items-center gap-1.5 justify-end [&>button]:shrink-0">
          <Button variant="outline" size="sm" className="rounded-full bg-gold border-gold text-navy font-bold" onClick={() => setTab("people")} data-testid="admin-book-for-parent"><Icons.CalendarPlus className="w-4 h-4 mr-1" />Book for a parent</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/staff/program")} data-testid="admin-goto-program">Program</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/staff")} data-testid="admin-goto-scanner">Scanner</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-turquoise/60 text-turquoise" onClick={() => nav("/staff/rock-talk")} data-testid="admin-goto-rocktalk">Rock Talk</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/staff/attendance")} data-testid="admin-goto-register">Register</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/staff/photos")} data-testid="admin-goto-gallery">Day Gallery</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/staff/transport")} data-testid="admin-goto-transport">Transport</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/staff/camp-roster")} data-testid="admin-goto-camp-roster">Camp Roster</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/staff/dinners")} data-testid="admin-goto-dinners">Today's Dinners</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/staff/lunches")} data-testid="admin-goto-lunches">Today's Lunches</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-gold/60 text-gold" onClick={() => nav("/staff/daytime")} data-testid="admin-goto-rock-crew">Rock Crew</Button>
          <Button variant="outline" size="sm" className="rounded-full bg-transparent border-white/30 text-white" onClick={() => nav("/dashboard")} data-testid="admin-goto-parent">Parent View</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="admin-logout"><Icons.LogOut className="w-5 h-5" /></Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-8">
        <h1 className="font-display font-extrabold text-2xl text-navy mb-4">Welcome, {user?.name}</h1>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto justify-start gap-1 bg-white border border-slate-200 rounded-xl p-1">
            {["overview", "daytime", "orders", "calendar", "pricing", "products", "dinner", "lunch", "camps", "program", "transport", "assignments", "parties", "credits", "people", "search", "waitlist", "prospect", "messages", "mailing", "support", "events"].map((t) => (
              <TabsTrigger key={t} value={t} className="capitalize rounded-lg data-[state=active]:bg-navy data-[state=active]:text-white" data-testid={`admin-tab-${t}`}>{t}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview"><StatsPanel onNavigate={setTab} /></TabsContent>
          <TabsContent value="daytime"><DaytimeAdmin /></TabsContent>
          <TabsContent value="orders"><OrdersPanel /></TabsContent>
          <TabsContent value="calendar"><CalendarPanel /></TabsContent>
          <TabsContent value="pricing"><PricingPanel /></TabsContent>
          <TabsContent value="products"><ProductsPanel /></TabsContent>
          <TabsContent value="dinner"><DinnerPanel /></TabsContent>
          <TabsContent value="lunch"><LunchPanel /></TabsContent>
          <TabsContent value="camps"><CampsPanel /></TabsContent>
          <TabsContent value="program"><ProgramPanel /></TabsContent>
          <TabsContent value="credits"><CreditsPanel /></TabsContent>
          <TabsContent value="people"><PeoplePanel /></TabsContent>
          <TabsContent value="search"><RegistrationsPanel /></TabsContent>
          <TabsContent value="waitlist"><WaitlistPanel /></TabsContent>
          <TabsContent value="prospect"><ProspectNotifyPanel /></TabsContent>
          <TabsContent value="messages"><MessagesPanel /></TabsContent>
          <TabsContent value="transport"><TransportPanel /></TabsContent>
          <TabsContent value="parties"><PartiesPanel /></TabsContent>
          <TabsContent value="mailing"><MailingListPanel /></TabsContent>
          <TabsContent value="support"><SupportAdmin /></TabsContent>
          <TabsContent value="events"><EventsAdmin /></TabsContent>
          <TabsContent value="assignments"><AssignmentsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function AssignmentsPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [kids, setKids] = useState([]);
  const [form, setForm] = useState({ staff_id: "", role: "watcher", child_ids: [], location: "", retriever_staff_id: "" });
  const load = () => api.get(`/admin/assignments?date=${date}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [date]);
  useEffect(() => { api.get("/admin/staff-list").then((r) => setStaff(r.data)); api.get("/admin/children-basic").then((r) => setKids(r.data)); }, []);

  const chosenRole = staff.find((s) => s.id === form.staff_id)?.role || form.role;
  const toggleKid = (id) => setForm((f) => ({ ...f, child_ids: f.child_ids.includes(id) ? f.child_ids.filter((x) => x !== id) : [...f.child_ids, id] }));
  const create = async () => {
    if (!form.staff_id) return toast.error("Pick a staff member");
    try { await api.post("/admin/assignments", { ...form, role: chosenRole, date }); toast.success("Assignment created"); setForm({ staff_id: "", role: "watcher", child_ids: [], location: "", retriever_staff_id: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/assignments/${id}`); load(); };

  return (
    <div className="mt-6 space-y-5">
      <div className="flex items-center gap-3">
        <Label className="text-xs">Date</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl w-44" data-testid="assign-date" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="assign-form">
        <p className="font-display font-bold text-navy">New assignment</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Staff member</Label>
            <select value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} className="w-full rounded-xl border border-slate-200 p-2 text-sm" data-testid="assign-staff">
              <option value="">Select…</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
            </select>
          </div>
          {chosenRole === "driver" && (<>
            <div><Label className="text-xs">Location / stop</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Warwick Academy" className="rounded-xl" data-testid="assign-location" /></div>
            <div><Label className="text-xs">Staff who retrieves kids</Label>
              <select value={form.retriever_staff_id} onChange={(e) => setForm({ ...form, retriever_staff_id: e.target.value })} className="w-full rounded-xl border border-slate-200 p-2 text-sm" data-testid="assign-retriever">
                <option value="">Select…</option>
                {staff.filter((s) => s.role !== "driver").map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
              </select>
            </div>
          </>)}
        </div>
        <div>
          <Label className="text-xs">Explorers ({form.child_ids.length} selected)</Label>
          <div className="max-h-52 overflow-y-auto grid sm:grid-cols-2 gap-1 mt-1 border border-slate-200 rounded-xl p-2">
            {kids.map((k) => (
              <label key={k.id} className={`flex items-center gap-2 text-sm rounded-lg px-2 py-1 cursor-pointer ${form.child_ids.includes(k.id) ? "bg-turquoise/15" : ""}`} data-testid={`assign-kid-${k.id}`}>
                <input type="checkbox" checked={form.child_ids.includes(k.id)} onChange={() => toggleKid(k.id)} className="w-4 h-4 accent-turquoise" />
                {k.name} <span className="text-slate-400 text-xs">{k.school}</span>
              </label>
            ))}
            {kids.length === 0 && <p className="text-slate-400 text-sm p-2">No explorers registered yet.</p>}
          </div>
        </div>
        <Button onClick={create} className="bg-navy rounded-full font-bold" data-testid="assign-create-btn"><Icons.Plus className="w-4 h-4 mr-1" /> Create assignment</Button>
      </div>

      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`assignment-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-navy">{r.staff_name} <span className={`text-xs px-2 py-0.5 rounded-full ${r.role === "driver" ? "bg-gold/20 text-gold-700" : "bg-turquoise/20 text-turquoise"}`}>{r.role}</span></p>
                {r.location && <p className="text-xs text-slate-500">📍 {r.location}{r.retriever_name ? ` · retrieved by ${r.retriever_name}` : ""}</p>}
                <p className="text-xs text-slate-500 mt-1">{(r.children || []).map((c) => `${c.first_name} ${c.last_name}`).join(", ") || "No explorers"}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-coral" onClick={() => del(r.id)} data-testid={`assign-del-${r.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-slate-400">No assignments for {date}.</p>}
      </div>
    </div>
  );
}

function StatsPanel({ onNavigate }) {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [s, setS] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  // default: clear everything EXCEPT the mailing list (kept by default)
  const [scopes, setScopes] = useState(Object.fromEntries(RESET_OPTS.map(([k]) => [k, k !== "mailing_list"])));
  const load = () => api.get("/admin/stats").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const selected = RESET_OPTS.filter(([k]) => scopes[k]).map(([k]) => k);
  const resetData = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: "RESET", scopes: selected });
      const total = Object.values(data.wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`Cleared ${total} records across ${selected.length} categories.`);
      setConfirmText("");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setResetting(false); }
  };
  if (!s) return null;
  const cards = [
    ["Total Explorers", s.total_children, "Users", "people"], ["Parents", s.total_parents, "UserCog", "people"],
    ["After-School Bookings", s.confirmed_afterschool ?? s.confirmed_bookings, "Ticket", "orders"], ["Camp Enrollments", s.camp_enrollments ?? 0, "Tent", "camps"],
    ["Credits Outstanding", s.credits_outstanding, "Sparkles", "credits"], ["Gold Memberships", s.memberships, "Crown", "orders"],
    ["Waitlisted", s.waitlist, "Clock", "waitlist"], ["Package Sales", money(s.package_sales), "DollarSign", "orders"],
    ["Camp Sales", money(s.camp_sales), "DollarSign", "orders"], ["Membership Sales", money(s.membership_sales), "DollarSign", "orders"],
    ["Wallet Deposits", money(s.wallet_deposits), "Wallet", "orders"], ["Snack Sales", money(s.snack_sales), "Cookie", "products"],
  ];
  return (
    <div className="mt-6">
      <p className="text-xs text-slate-400 mb-3">Tap any card to jump straight to its section.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([l, v, ic, target]) => { const Icon = Icons[ic]; return (
          <button key={l} onClick={() => onNavigate && onNavigate(target)} className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-turquoise hover:shadow-md transition-all group" data-testid={`admin-stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between mb-2"><Icon className="w-5 h-5 text-turquoise" /><Icons.ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-turquoise transition-colors" /></div>
            <p className="font-display font-extrabold text-2xl text-navy">{v}</p>
            <p className="text-xs text-slate-400">{l}</p>
          </button>
        ); })}
      </div>

      <WaiverReport />
      <LateFeeReport />

      {isOwner && (
      <div className="mt-8 bg-white rounded-2xl border-2 border-red-200 p-5" data-testid="admin-danger-zone">
        <div className="flex items-center gap-2 text-red-600 mb-1"><Icons.AlertTriangle className="w-5 h-5" /><p className="font-display font-extrabold">Danger Zone</p></div>
        <p className="text-sm text-slate-500 mb-4">Choose exactly which data to permanently delete. Anything you leave <strong>unticked is kept</strong> (e.g. your mailing-list subscribers). Your admin/staff/driver logins, settings, calendar dates, schools and snack products are always kept. This cannot be undone.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          {RESET_OPTS.map(([k, label]) => (
            <label key={k} className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition-colors ${scopes[k] ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={`reset-scope-${k}`}>
              <input type="checkbox" checked={scopes[k]} onChange={(e) => setScopes((p) => ({ ...p, [k]: e.target.checked }))} className="w-4 h-4 accent-red-600" data-testid={`reset-scope-${k}-input`} />
              <span className="text-sm text-navy">{scopes[k] ? "Clear" : "Keep"} — {label}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-slate-500 mb-1">Type <span className="font-mono font-bold text-red-600">RESET</span> to enable the button:</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="RESET" className="w-40 rounded-xl" data-testid="reset-confirm-input" />
          <Button onClick={resetData} disabled={confirmText !== "RESET" || selected.length === 0 || resetting} className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-full disabled:opacity-40" data-testid="reset-data-btn">
            {resetting ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : `Clear selected data (${selected.length})`}
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}

const ORDER_KIND = { event: "Event Booking", package: "After-School Package", camp: "Camp Registration", wallet: "Wallet Top-Up", membership: "Gold Membership", dinner: "Explorer Dinner Club" };
const TRANSPORT_LABEL = { pickup: "Pick-up", dropoff: "Drop-off", both: "Pick-up + Drop-off", none: "—" };

function OrderDetail({ order, onClose }) {
  if (!order) return null;
  const meta = order.meta || {};
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
        <DialogHeader><DialogTitle className="font-display">{ORDER_KIND[order.kind] || order.kind} · {money(order.amount)}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2 items-center">
            <Badge className={`border-0 ${order.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : order.status === "awaiting_confirmation" ? "bg-amber-100 text-amber-700" : order.status === "rejected" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{order.status.replace(/_/g, " ")}</Badge>
            {order.payment_method && <Badge className="bg-slate-100 text-slate-600 border-0">via {({ woi: "WOI", bank: "bank transfer", cash: "cash", card: "card" })[order.payment_method] || order.payment_method}</Badge>}
            {order.reference && <span className="font-mono font-bold text-turquoise" data-testid="detail-ref">{order.reference}</span>}
          </div>
          <div className="bg-slate-50 rounded-xl p-3">
            <p><b>Parent:</b> {order.parent_name}{order.parent_phone ? ` · ${order.parent_phone}` : ""}</p>
            {order.parent_email && <p className="text-slate-500 text-xs">{order.parent_email}</p>}
            {order.child_name && <p className="mt-1"><b>Explorer:</b> {order.child_name} <span className="font-mono text-turquoise text-xs">{order.explorer_id}</span></p>}
            <p className="text-slate-400 text-xs mt-1">Ordered {new Date(order.created_at).toLocaleString()}{order.confirmed_at ? ` · Confirmed ${new Date(order.confirmed_at).toLocaleString()}` : ""}</p>
          </div>
          {order.kind === "camp" && <p className="font-semibold text-navy">{meta.camp_name} · {(meta.option || "").replace("day", "-day")}</p>}
          {order.kind === "dinner" && <p className="font-semibold text-navy">🍽 {meta.meal_name}{meta.date ? ` · ${new Date(meta.date + "T12:00").toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" })}` : ""}</p>}
          {order.kind === "package" && <p className="font-semibold text-navy capitalize">{meta.package_type} package</p>}
          {(order.line_items || []).length > 0 && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500 flex justify-between"><span>Day ({order.line_items.length})</span><span>Transport</span></div>
              {order.line_items.map((li, i) => (
                <div key={i} className="px-3 py-2 flex justify-between items-center border-t border-slate-100" data-testid={`order-line-${i}`}>
                  <div><p className="font-medium text-navy">{new Date(li.date + "T12:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" })}</p>{li.addons?.length > 0 && <p className="text-[11px] text-slate-400">Add-ons: {li.addons.join(", ")}</p>}</div>
                  <div className="text-right"><p className="text-sm text-navy">{TRANSPORT_LABEL[li.transport] || li.transport}</p>{li.transport_cost > 0 && <p className="text-[11px] text-slate-400">+{money(li.transport_cost)}</p>}</div>
                </div>
              ))}
            </div>
          )}
          {order.kind === "wallet" && <p className="text-slate-600">Adventure Wallet top-up of {money(order.amount)}.</p>}
          {order.kind === "membership" && <p className="text-slate-600">Gold membership — {meta.plan === "school_year" ? "School Year 2026/27" : "Term 1 2026"} plan.</p>}
          <div className="flex justify-between font-bold text-navy border-t border-slate-200 pt-2"><span>Total</span><span>{money(order.amount)}</span></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [detail, setDetail] = useState(null);
  const openDetail = async (id) => { try { const { data } = await api.get(`/admin/orders/${id}`); setDetail(data); } catch (e) { toast.error(apiErr(e)); } };
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action, msg) => {
    try { await api.post(`/admin/orders/${id}/${action}`); toast.success(msg || `Order ${action}ed`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const [waiveFor, setWaiveFor] = useState(null);
  const waive = (o) => setWaiveFor(o);
  const del = async (id) => {
    try { await api.delete(`/admin/orders/${id}`); toast.success("Order deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remind = async (id) => {
    try { await api.post(`/admin/orders/${id}/remind`); toast.success("Payment reminder sent"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const resend = async (o) => {
    if (!window.confirm(`Resend the payment confirmation email to ${o.parent_name}?`)) return;
    try { const { data } = await api.post(`/admin/orders/${o.id}/resend-receipt`); toast.success(`Confirmation ${data.number} re-sent to ${data.email} — also saved in their Documents`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const unconfirm = async (o) => {
    if (!window.confirm(`Undo the confirmation of this ${o.kind} order (${money(o.amount)}) for ${o.parent_name}?\n\nIt goes back to unpaid, anything it unlocked (bookings, credits, wallet money, passes) is reversed, and the invoice is removed from their Documents. The parent is not notified.`)) return;
    try { await api.post(`/admin/orders/${o.id}/unconfirm`); toast.success("Confirmation undone — order is unpaid again"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remindAll = async () => {
    try { const { data } = await api.post("/admin/orders/remind-all"); toast.success(`Reminder sent to ${data.reminded} pending order${data.reminded === 1 ? "" : "s"}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const pendingCount = orders.filter((o) => o.status === "pending").length;
  return (
    <div className="mt-6 space-y-3">
      <WaiveDialog order={waiveFor} onClose={() => setWaiveFor(null)} onDone={() => { setWaiveFor(null); load(); }} />
      {pendingCount > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-2xl p-3">
          <p className="text-sm text-amber-700 font-semibold">{pendingCount} order{pendingCount === 1 ? "" : "s"} awaiting payment</p>
          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 rounded-full font-bold" onClick={remindAll} data-testid="remind-all-btn"><Icons.BellRing className="w-4 h-4 mr-1" /> Remind all pending</Button>
        </div>
      )}
      {orders.map((o) => (
        <div key={o.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`admin-order-${o.id}`}>
          <button onClick={() => openDetail(o.id)} className="text-left flex-1 min-w-0 group" data-testid={`order-view-${o.id}`}><p className="font-semibold text-navy capitalize group-hover:text-turquoise transition-colors">{o.kind} · {money(o.amount)}{o.payment_method ? <span className="text-xs font-normal text-slate-400 normal-case"> · via {({ woi: "WOI", bank: "bank transfer", cash: "cash", card: "card" })[o.payment_method] || o.payment_method}</span> : ""} <Icons.ChevronRight className="w-3.5 h-3.5 inline text-slate-300" /></p><p className="text-sm text-slate-500">{o.parent_name} · {new Date(o.created_at).toLocaleString()}</p>{o.reference && <p className="text-xs font-mono font-bold text-turquoise mt-0.5" data-testid={`order-ref-${o.id}`}>Ref: {o.reference}</p>}{o.payment_due && o.status === "pending" && <p className={`text-xs font-semibold mt-0.5 ${o.overdue ? "text-red-600" : "text-amber-600"}`} data-testid={`order-due-${o.id}`}>{o.overdue ? "⚠ Overdue —" : "Due"} {new Date(o.payment_due).toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" })}</p>}</button>
          <div className="flex items-center gap-2">
            <Badge className={`border-0 ${o.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : o.status === "awaiting_confirmation" ? "bg-amber-100 text-amber-700" : o.status === "rejected" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{o.status.replace(/_/g, " ")}</Badge>
            {o.booking_confirmed_at && o.status !== "confirmed" && <Badge className="border-0 bg-sky-100 text-sky-700" data-testid={`booking-confirmed-badge-${o.id}`}>booking confirmed · payment due</Badge>}
            {o.waived && <Badge className="border-0 bg-violet-100 text-violet-700" title={o.waive_reason || ""} data-testid={`waived-badge-${o.id}`}>waived {money(o.original_amount)}</Badge>}
            {o.status === "confirmed" && (
              <Button size="sm" variant="outline" className="rounded-full text-turquoise border-turquoise/40" onClick={() => resend(o)} title={o.receipt_resent_at ? `Last re-sent ${new Date(o.receipt_resent_at).toLocaleString()}` : ""} data-testid={`resend-receipt-${o.id}`}><Icons.Mail className="w-4 h-4 mr-1" /> Resend confirmation{o.receipt_resend_count ? ` (${o.receipt_resend_count})` : ""}</Button>
            )}
            {o.status === "confirmed" && (
              <Button size="sm" variant="outline" className="rounded-full text-slate-600" onClick={() => unconfirm(o)} data-testid={`unconfirm-order-${o.id}`}><Icons.Undo2 className="w-4 h-4 mr-1" /> Undo confirm</Button>
            )}
            {o.status === "pending" && (
              <Button size="sm" variant="outline" className="rounded-full text-amber-600 border-amber-300" onClick={() => remind(o.id)} data-testid={`remind-order-${o.id}`}><Icons.BellRing className="w-4 h-4 mr-1" /> Remind</Button>
            )}
            {["awaiting_confirmation", "pending"].includes(o.status) && (
              <>
                {!o.booking_confirmed_at
                  ? <Button size="sm" className="bg-sky-500 hover:bg-sky-600 rounded-full" onClick={() => act(o.id, "confirm-booking", "Booking confirmed — parent notified with directions")} data-testid={`confirm-booking-${o.id}`}><Icons.CalendarCheck className="w-4 h-4 mr-1" /> Confirm booking</Button>
                  : <Button size="sm" variant="ghost" className="rounded-full text-slate-400" onClick={() => act(o.id, "unconfirm-booking", "Booking confirmation undone")} data-testid={`unconfirm-booking-${o.id}`}><Icons.Undo2 className="w-4 h-4 mr-1" /> Undo booking</Button>}
                <Button size="sm" className="bg-emerald-500 rounded-full" onClick={() => act(o.id, "confirm")} data-testid={`confirm-order-${o.id}`}><Icons.BadgeDollarSign className="w-4 h-4 mr-1" /> Confirm payment</Button>
                <Button size="sm" variant="outline" className="rounded-full text-violet-700 border-violet-300" onClick={() => waive(o)} data-testid={`waive-order-${o.id}`}><Icons.Gift className="w-4 h-4 mr-1" /> Waive fee</Button>
                <Button size="sm" variant="outline" className="rounded-full text-coral" onClick={() => act(o.id, "reject")} data-testid={`reject-order-${o.id}`}>Reject</Button>
              </>
            )}
            {confirmDel === o.id ? (
              <>
                <Button size="sm" className="bg-red-600 hover:bg-red-700 rounded-full font-bold" onClick={() => del(o.id)} data-testid={`confirm-delete-order-${o.id}`}>Delete forever</Button>
                <Button size="sm" variant="ghost" className="rounded-full text-slate-500" onClick={() => setConfirmDel(null)} data-testid={`cancel-delete-order-${o.id}`}>Cancel</Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600 rounded-full" onClick={() => setConfirmDel(o.id)} data-testid={`delete-order-${o.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
            )}
          </div>
          <OrderHistory order={o} />
        </div>
      ))}
      {orders.length === 0 && <p className="text-slate-400">No payment orders yet.</p>}
      <OrderDetail order={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function CalendarPanel() {
  const [days, setDays] = useState([]);
  const load = () => api.get("/admin/calendar").then((r) => setDays(r.data));
  useEffect(() => { load(); }, []);
  const update = async (day, patch) => {
    try { await api.put(`/admin/calendar/${day}`, patch); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {days.map((d) => (
        <div key={d.date} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`admin-day-${d.date}`}>
          <p className="font-semibold text-navy">{new Date(d.date + "T12:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" })}</p>
          <p className="text-xs text-slate-400 mb-2">{d.booked_count}/{d.capacity} booked</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant={d.status === "open" ? "default" : "outline"} className="rounded-full" onClick={() => update(d.date, { status: d.status === "open" ? "closed" : "open" })} data-testid={`toggle-day-${d.date}`}>{d.status === "open" ? "Open" : "Closed"}</Button>
            <Input type="number" defaultValue={d.capacity} onBlur={(e) => update(d.date, { capacity: parseInt(e.target.value) })} className="w-20 rounded-xl h-9" data-testid={`capacity-${d.date}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function PricingPanel() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setPrice = (k, v) => setS({ ...s, pricing: { ...s.pricing, [k]: parseFloat(v) || 0 } });
  const toggleLaunch = async (open) => {
    try { await api.post("/admin/launch-mode", { open }); setS({ ...s, registration_live: open }); toast.success(open ? "Launch Mode ON — registration open & parents notified" : "Launch Mode OFF — mailing list signups only"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-6 max-w-lg space-y-4">
      {isOwner ? (
      <div className="bg-white rounded-2xl border-2 border-gold p-5 flex items-center justify-between gap-4" data-testid="launch-mode-card">
        <div>
          <p className="font-display font-extrabold text-navy">Launch Mode {s.registration_live ? "· LIVE" : ""}</p>
          <p className="text-xs text-slate-500 max-w-xs">ON: the site invites parents to pre-register and every parent gets an in-app announcement. Every pre-launch mailing-list subscriber is automatically emailed a personal Early Bird discount code (expires Oct 27). OFF: guests can only join the mailing list.</p>
        </div>
        <Switch checked={!!s.registration_live} onCheckedChange={toggleLaunch} data-testid="launch-mode-toggle" />
      </div>
      ) : (
      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 text-sm text-slate-500" data-testid="owner-only-note">Launch Mode, pricing and payment changes are owner-only. Ask the account owner to make these changes.</div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="cutoff-card">
        <p className="font-display font-extrabold text-navy">Same-Day Transport Cutoff</p>
        <p className="text-xs text-slate-500 mb-2">Latest time (Bermuda) parents can add transport for that same afterschool day. Saved with the button below.</p>
        <Input type="time" value={s.transport?.cutoff_time || "14:30"} onChange={(e) => setS({ ...s, transport: { ...(s.transport || {}), cutoff_time: e.target.value } })} className="rounded-xl w-40" data-testid="cutoff-input" />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 grid grid-cols-2 gap-3">
        {["daily", "weekly", "monthly", "membership"].map((k) => (
          <div key={k}><Label className="text-xs capitalize">{k} price</Label><Input type="number" value={s.pricing[k]} onChange={(e) => setPrice(k, e.target.value)} className="rounded-xl" data-testid={`price-${k}`} /></div>
        ))}
        <div><Label className="text-xs">Default capacity</Label><Input type="number" value={s.capacity_default} onChange={(e) => setS({ ...s, capacity_default: parseInt(e.target.value) })} className="rounded-xl" data-testid="default-capacity" /></div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <Label className="text-xs">Promo codes (comma: CODE:percent)</Label>
        <Input defaultValue={(s.promo_codes || []).map((p) => `${p.code}:${p.percent}`).join(", ")}
          onBlur={(e) => setS({ ...s, promo_codes: e.target.value.split(",").filter(Boolean).map((x) => { const [code, percent] = x.trim().split(":"); return { code, percent: parseFloat(percent) || 0, active: true }; }) })}
          className="rounded-xl" data-testid="promo-codes" placeholder="EARLYBIRD:10, FRIEND:5" />
      </div>
      {isOwner && <Button onClick={save} className="bg-navy rounded-full font-bold" data-testid="save-pricing-btn">Save Settings</Button>}
    </div>
  );
}

function ProgramPanel() {
  const [view, setView] = useState("day");
  const [date, setDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; });
  const [day, setDay] = useState(null);
  const [staff, setStaff] = useState([]);
  const [acts, setActs] = useState({ categories: [], activities: [] });
  const [tmpls, setTmpls] = useState([]);
  const [records, setRecords] = useState([]);
  const [copyFrom, setCopyFrom] = useState("");
  const empty = { category: "Inflatable Activities", name: "", age_group: "", duration: "", materials: "", instructions: "", num_children: "", indoor_outdoor: "Indoor", safety_notes: "" };
  const [af, setAf] = useState(empty);
  const [aedit, setAedit] = useState(null);

  const loadDay = () => api.get(`/program/day?date=${date}`).then((r) => setDay(r.data)).catch((e) => toast.error(apiErr(e)));
  const loadActs = () => api.get("/activities").then((r) => setActs(r.data));
  useEffect(() => { api.get("/program/staff-members").then((r) => setStaff(r.data)); loadActs(); }, []);
  useEffect(() => { if (view === "day") loadDay(); if (view === "templates") api.get("/admin/schedule-templates?program_type=afterschool").then((r) => setTmpls(r.data)); if (view === "records") api.get("/admin/program/records").then((r) => setRecords(r.data)); }, [view, date]);  // eslint-disable-line

  const saveRoles = async (roles) => { try { await api.post(`/program/day/${date}/roles`, roles); toast.success("Roles saved"); loadDay(); } catch (e) { toast.error(apiErr(e)); } };
  const savePlan = async (bid, planned_activity) => { try { await api.post(`/program/day/${date}/block/${bid}`, { planned_activity }); } catch (e) { toast.error(apiErr(e)); } };
  const doCopy = async () => { try { await api.post(`/admin/program/day/${date}/copy-from`, { source_date: copyFrom }); toast.success("Plan copied"); loadDay(); } catch (e) { toast.error(apiErr(e)); } };
  const saveAct = async () => { try { if (aedit) await api.put(`/admin/activities/${aedit}`, af); else await api.post("/admin/activities", af); toast.success("Activity saved"); setAf(empty); setAedit(null); loadActs(); } catch (e) { toast.error(apiErr(e)); } };
  const delAct = async (id) => { await api.delete(`/admin/activities/${id}`); loadActs(); };
  const saveTmpl = async (t) => { try { await api.put(`/admin/schedule-templates/${t.id}`, { theme_tag: t.theme_tag, blocks: t.blocks }); toast.success("Template saved"); } catch (e) { toast.error(apiErr(e)); } };

  const roles = day?.roles || {};
  const TABS = [["day", "Day & Planner"], ["library", "Activity Library"], ["templates", "Schedule Templates"], ["records", "Completion Records"]];

  return (
    <div className="mt-6 space-y-4">
      <div className="flex gap-2 flex-wrap">{TABS.map(([k, l]) => <button key={k} onClick={() => setView(k)} data-testid={`program-tab-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${view === k ? "bg-navy text-white" : "bg-slate-100 text-slate-500"}`}>{l}</button>)}</div>

      {view === "day" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl w-auto" data-testid="program-admin-date" />
            <span className="text-sm font-semibold text-navy">{day?.no_program ? "No program this day" : day?.theme_tag}</span>
          </div>
          {day && !day.no_program && (
            <>
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="font-display font-bold text-navy mb-2">Daily Staff Roles</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {[["lead", "Lead Explorer"], ["activity", "Activity Explorer"], ["support", "Support Explorer"]].map(([rk, rl]) => (
                    <div key={rk}>
                      <Label className="text-xs">{rl}</Label>
                      <select value={roles[rk] || ""} onChange={(e) => saveRoles({ ...roles, [rk]: e.target.value })} className="w-full rounded-xl border border-slate-200 h-10 px-2 text-sm" data-testid={`role-${rk}`}>
                        <option value="">— Unassigned —</option>
                        {staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <p className="font-display font-bold text-navy">Weekly Activity Planner</p>
                  <div className="flex items-center gap-1"><Input type="date" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} className="rounded-xl w-auto h-9 text-xs" data-testid="program-copy-date" /><Button size="sm" variant="outline" className="rounded-full" onClick={doCopy} data-testid="program-copy-btn">Copy plan</Button></div>
                </div>
                <datalist id="act-list">{acts.activities.map((a) => <option key={a.id} value={a.name} />)}</datalist>
                <div className="space-y-2">
                  {(day.blocks || []).filter((b) => b.type === "main").map((b) => (
                    <div key={b.id} className="flex items-center gap-2" data-testid={`plan-${b.id}`}>
                      <span className="text-xs text-slate-500 w-40 shrink-0">{b.start} · {b.title}</span>
                      <Input list="act-list" defaultValue={b.planned_activity} placeholder="Planned activity…" onBlur={(e) => savePlan(b.id, e.target.value)} className="rounded-xl" data-testid={`plan-input-${b.id}`} />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {view === "library" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2 h-fit">
            <p className="font-display font-bold text-navy">{aedit ? "Edit activity" : "New activity"}</p>
            <select value={af.category} onChange={(e) => setAf({ ...af, category: e.target.value })} className="w-full rounded-xl border border-slate-200 h-10 px-2 text-sm" data-testid="act-category">{acts.categories.map((c) => <option key={c}>{c}</option>)}</select>
            <Input value={af.name} onChange={(e) => setAf({ ...af, name: e.target.value })} placeholder="Activity name" className="rounded-xl" data-testid="act-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={af.age_group} onChange={(e) => setAf({ ...af, age_group: e.target.value })} placeholder="Age group" className="rounded-xl" data-testid="act-age" />
              <Input value={af.duration} onChange={(e) => setAf({ ...af, duration: e.target.value })} placeholder="Duration" className="rounded-xl" data-testid="act-duration" />
              <Input value={af.num_children} onChange={(e) => setAf({ ...af, num_children: e.target.value })} placeholder="# children" className="rounded-xl" data-testid="act-num" />
              <select value={af.indoor_outdoor} onChange={(e) => setAf({ ...af, indoor_outdoor: e.target.value })} className="rounded-xl border border-slate-200 h-10 px-2 text-sm" data-testid="act-io"><option>Indoor</option><option>Outdoor</option><option>Both</option></select>
            </div>
            <Input value={af.materials} onChange={(e) => setAf({ ...af, materials: e.target.value })} placeholder="Materials required" className="rounded-xl" data-testid="act-materials" />
            <Textarea value={af.instructions} onChange={(e) => setAf({ ...af, instructions: e.target.value })} placeholder="Instructions" rows={2} className="rounded-xl" data-testid="act-instructions" />
            <Input value={af.safety_notes} onChange={(e) => setAf({ ...af, safety_notes: e.target.value })} placeholder="Safety notes" className="rounded-xl" data-testid="act-safety" />
            <div className="flex gap-2"><Button onClick={saveAct} className="bg-turquoise text-navy rounded-full font-bold" data-testid="act-save">{aedit ? "Update" : "Add activity"}</Button>{aedit && <Button variant="outline" className="rounded-full" onClick={() => { setAf(empty); setAedit(null); }}>Cancel</Button>}</div>
          </div>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {acts.categories.map((cat) => {
              const list = acts.activities.filter((a) => a.category === cat);
              if (!list.length) return null;
              return (
                <div key={cat}>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mt-2">{cat}</p>
                  {list.map((a) => (
                    <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-2.5 flex justify-between items-center" data-testid={`act-${a.id}`}>
                      <div><p className="text-sm font-semibold text-navy">{a.name}</p><p className="text-[11px] text-slate-400">{[a.age_group, a.duration, a.indoor_outdoor].filter(Boolean).join(" · ")}</p></div>
                      <div className="flex gap-1"><Button variant="ghost" size="icon" onClick={() => { setAf(a); setAedit(a.id); }} data-testid={`act-edit-${a.id}`}><Icons.Pencil className="w-4 h-4" /></Button><Button variant="ghost" size="icon" className="text-coral" onClick={() => delAct(a.id)} data-testid={`act-del-${a.id}`}><Icons.Trash2 className="w-4 h-4" /></Button></div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "templates" && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Edit the recurring after-school theme names and block times. Changes apply to future days (already-opened days keep their saved schedule).</p>
          {tmpls.map((t, ti) => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`tmpl-${t.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold text-slate-400 w-24">{["Mon", "Tue", "Wed", "Thu", "Fri"][t.weekday] || "Camp"}</span>
                <Input value={t.theme_tag} onChange={(e) => { const c = [...tmpls]; c[ti] = { ...t, theme_tag: e.target.value }; setTmpls(c); }} className="rounded-xl font-bold" data-testid={`tmpl-theme-${t.id}`} />
                <Button size="sm" className="bg-navy rounded-full" onClick={() => saveTmpl(tmpls[ti])} data-testid={`tmpl-save-${t.id}`}>Save</Button>
              </div>
              <div className="space-y-1">
                {t.blocks.map((b, bi) => (
                  <div key={b.id} className="flex items-center gap-1 text-sm">
                    <Input value={b.start} onChange={(e) => { const c = [...tmpls]; c[ti].blocks[bi] = { ...b, start: e.target.value }; setTmpls(c); }} className="rounded-lg w-24 h-8 text-xs" />
                    <Input value={b.end} onChange={(e) => { const c = [...tmpls]; c[ti].blocks[bi] = { ...b, end: e.target.value }; setTmpls(c); }} className="rounded-lg w-24 h-8 text-xs" />
                    <Input value={b.title} onChange={(e) => { const c = [...tmpls]; c[ti].blocks[bi] = { ...b, title: e.target.value }; setTmpls(c); }} className="rounded-lg h-8 text-xs" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "records" && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="program-records">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Theme</th><th className="p-2">Blocks done</th><th className="p-2">Prep</th><th className="p-2">Closing</th></tr></thead>
            <tbody>{records.map((r) => (
              <tr key={r.date} className="border-t border-slate-100" data-testid={`record-${r.date}`}><td className="p-2 font-semibold text-navy">{r.date}</td><td className="p-2">{r.theme_tag}</td><td className="p-2 text-center">{r.completed}/{r.total}</td><td className="p-2 text-center">{r.prep_done}/8</td><td className="p-2 text-center">{r.closing_done}/7</td></tr>
            ))}{records.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-slate-400">No program records yet.</td></tr>}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CampsPanel() {
  const empty = { name: "", emoji: "🍂", tagline: "", description: "", dates: "", day_options: "3,5", capacity: 30, transport_enabled: true, status: "open" };
  const [camps, setCamps] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/admin/camps").then((r) => setCamps(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      name: form.name, emoji: form.emoji, tagline: form.tagline, description: form.description,
      dates: (form.dates || "").split(",").map((s) => s.trim()).filter(Boolean),
      day_options: (form.day_options || "").split(",").map((s) => parseInt(s.trim())).filter((n) => n > 0),
      capacity: parseInt(form.capacity) || 0, transport_enabled: !!form.transport_enabled, status: form.status,
      day_price: parseFloat(form.day_price) > 0 ? parseFloat(form.day_price) : null,
    };
    if (!payload.name || payload.dates.length === 0) return toast.error("Name and at least one date are required");
    try {
      if (editId) await api.put(`/admin/camps/${editId}`, payload);
      else await api.post("/admin/camps", payload);
      toast.success("Camp saved"); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const edit = (c) => { setEditId(c.id); setForm({ ...c, dates: (c.dates || []).join(", "), day_options: (c.day_options || []).join(",") }); };
  const del = async (id) => { if (!window.confirm("Delete this camp? Existing registrations are kept.")) return; await api.delete(`/admin/camps/${id}`); load(); };
  const toggle = async (c) => { await api.put(`/admin/camps/${c.id}`, { ...c, dates: c.dates, day_options: c.day_options, status: c.status === "open" ? "closed" : "open" }); load(); };
  const announce = async () => {
    if (!window.confirm("Send an in-app announcement about all OPEN camps to every registered parent?")) return;
    try { const { data } = await api.post("/admin/announce-camps"); toast.success(`Announced ${data.camps} camp(s) to ${data.parents_notified} parents`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="bg-navy text-white rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
        <div><p className="font-display font-bold">Share camps with parents</p><p className="text-white/60 text-xs">Sends an in-app dashboard announcement of all open camps (dates, options & a register link) to every registered parent.</p></div>
        <Button onClick={announce} className="bg-gold text-navy font-bold rounded-full shrink-0" data-testid="announce-camps-btn"><Icons.Megaphone className="w-4 h-4 mr-1" /> Announce to Parents</Button>
      </div>
      <CampEmailCopy camps={camps} />
      <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3 h-fit">
        <p className="font-display font-bold text-navy">{editId ? "Edit camp" : "New camp"}</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="camp-form-name" /></div>
          <div><Label className="text-xs">Emoji</Label><Input value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} className="rounded-xl" data-testid="camp-form-emoji" /></div>
          <div><Label className="text-xs">Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="rounded-xl" data-testid="camp-form-capacity" /></div>
          <div className="col-span-2"><Label className="text-xs">Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="rounded-xl" data-testid="camp-form-tagline" /></div>
          <div className="col-span-2"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-xl" data-testid="camp-form-desc" /></div>
          <div className="col-span-2"><Label className="text-xs">Dates (comma-separated, YYYY-MM-DD)</Label><Input value={form.dates} onChange={(e) => setForm({ ...form, dates: e.target.value })} placeholder="2026-10-19, 2026-10-20, ..." className="rounded-xl" data-testid="camp-form-dates" /></div>
          <div><Label className="text-xs">Day options</Label><Input value={form.day_options} onChange={(e) => setForm({ ...form, day_options: e.target.value })} placeholder="3,5" className="rounded-xl" data-testid="camp-form-options" /></div>
          <div><Label className="text-xs">Price per day ($, blank = standard rate)</Label><Input type="number" value={form.day_price ?? ""} onChange={(e) => setForm({ ...form, day_price: e.target.value })} placeholder="Standard" className="rounded-xl" data-testid="camp-form-day-price" /></div>
          <div><Label className="text-xs">Status</Label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-xl border border-slate-200 h-10 px-2 text-sm" data-testid="camp-form-status"><option value="open">Open</option><option value="closed">Closed</option></select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={form.transport_enabled} onCheckedChange={(v) => setForm({ ...form, transport_enabled: v })} data-testid="camp-form-transport" /> Transport available</label>
        <div className="flex gap-2">
          <Button onClick={save} className="bg-turquoise text-navy rounded-full font-bold" data-testid="camp-save-btn">{editId ? "Update Camp" : "Create Camp"}</Button>
          {editId && <Button variant="outline" onClick={() => { setForm(empty); setEditId(null); }} className="rounded-full" data-testid="camp-cancel-edit">Cancel</Button>}
        </div>
      </div>
      <div className="space-y-3">
        {camps.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`admin-camp-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display font-bold text-navy">{c.emoji} {c.name} {c.status === "closed" && <span className="text-xs text-coral">(closed)</span>}</p>
                <p className="text-xs text-slate-500">{c.dates.length} days · {c.dates[0]} → {c.dates[c.dates.length - 1]} · options {(c.day_options || []).join("/")}-day</p>
                <p className="text-xs text-slate-400 mt-0.5">{c.registered} registered · {c.spots_left != null ? `${c.spots_left} spots left` : "unlimited"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => toggle(c)} data-testid={`camp-toggle-${c.id}`}>{c.status === "open" ? "Close" : "Open"}</Button>
                <Button variant="ghost" size="icon" onClick={() => edit(c)} data-testid={`camp-edit-${c.id}`}><Icons.Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="text-coral" onClick={() => del(c.id)} data-testid={`camp-del-${c.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
        {camps.length === 0 && <p className="text-slate-400">No camps yet. Create one to get started.</p>}
      </div>
      </div>
    </div>
  );
}

function DinnerMealCard({ m, onSaved }) {
  const [f, setF] = useState({ name: m.name, description: m.description || "", price: m.price || 0, allergens: m.allergens || "", max_qty: m.max_qty ?? "", active: m.active, sold_out: !!m.sold_out, sides: (m.sides || []).join(", ") });
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setDirty(true); };
  const save = async () => {
    try { await api.put(`/admin/dinner/meals/${m.id}`, { ...f, price: parseFloat(f.price) || 0, max_qty: f.max_qty === "" ? null : parseInt(f.max_qty), portions: m.portions || [], sides: f.sides.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Meal saved"); setDirty(false); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => { await api.delete(`/admin/dinner/meals/${m.id}`); onSaved(); };
  return (
    <div className={`bg-white rounded-2xl border p-4 ${f.sold_out ? "border-coral/40" : "border-slate-200"}`} data-testid={`dinner-meal-${m.id}`}>
      <div className="flex items-center gap-2">
        <Input value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-lg h-8 font-semibold" data-testid={`meal-name-${m.id}`} />
        <Button variant="ghost" size="icon" className="text-coral shrink-0" onClick={del} data-testid={`meal-del-${m.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
      </div>
      <Input value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" className="rounded-lg h-8 mt-2 text-sm" data-testid={`meal-desc-${m.id}`} />
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div><Label className="text-[10px]">Price ($)</Label><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="rounded-lg h-8" data-testid={`meal-price-${m.id}`} /></div>
        <div><Label className="text-[10px]">Max qty/day</Label><Input type="number" value={f.max_qty} onChange={(e) => set("max_qty", e.target.value)} placeholder="∞" className="rounded-lg h-8" data-testid={`meal-qty-${m.id}`} /></div>
      </div>
      <Input value={f.allergens} onChange={(e) => set("allergens", e.target.value)} placeholder="Allergen notes" className="rounded-lg h-8 mt-2 text-sm" data-testid={`meal-allergens-${m.id}`} />
      <div className="mt-2"><Label className="text-[10px]">Sides (comma-separated — parents pick one)</Label><Input value={f.sides} onChange={(e) => set("sides", e.target.value)} placeholder="e.g. Fries, Salad, Rice" className="rounded-lg h-8 text-sm" data-testid={`meal-sides-${m.id}`} /></div>
      <div className="flex gap-4 mt-2 text-sm">
        <label className="flex items-center gap-1"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid={`meal-active-${m.id}`} /> Active</label>
        <label className="flex items-center gap-1"><input type="checkbox" checked={f.sold_out} onChange={(e) => set("sold_out", e.target.checked)} data-testid={`meal-soldout-${m.id}`} /> Sold out</label>
      </div>
      {dirty && <Button size="sm" onClick={save} className="bg-turquoise text-navy rounded-full font-bold mt-2 w-full" data-testid={`meal-save-${m.id}`}>Save</Button>}
    </div>
  );
}

function DinnerPanel() {
  const [meals, setMeals] = useState([]);
  const [requests, setRequests] = useState([]);
  const [name, setName] = useState("");
  const [menuDate, setMenuDate] = useState("");
  const [menuIds, setMenuIds] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [report, setReport] = useState(null);
  const load = () => {
    api.get("/admin/dinner/meals").then((r) => setMeals(r.data));
    api.get("/admin/dinner/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/admin/dinner/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/dinner/report").then((r) => setReport(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const saveCfg = async () => { try { await api.put("/admin/dinner/config", { packs: cfg.packs, combo: cfg.combo }); toast.success("Dinner plans saved"); } catch (e) { toast.error(apiErr(e)); } };
  const setReqStatus = async (id, status) => { try { await api.put(`/admin/dinner/requests/${id}`, { status }); toast.success("Request updated"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const addMeal = async () => { if (!name) return; await api.post("/admin/dinner/meals", { name, price: 0 }); setName(""); load(); };
  const loadMenu = async (d) => { setMenuDate(d); if (!d) return; try { const { data } = await api.get(`/admin/dinner/menu?date=${d}`); setMenuIds(data.meal_ids || []); } catch (e) { toast.error(apiErr(e)); } };
  const toggleMenu = (id) => setMenuIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const saveMenu = async () => { try { await api.put("/admin/dinner/menu", { date: menuDate, meal_ids: menuIds }); toast.success("Day menu saved"); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="mt-6 space-y-6">
      <div className="bg-navy text-white rounded-2xl p-4">
        <p className="font-display font-extrabold">EXPLORER DINNER CLUB</p>
        <p className="text-white/60 text-xs">PLAY. LEARN. EAT. HOME. — set meals, prices &amp; which 2–3 options run each program day.</p>
      </div>

      {cfg && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="dinner-packs-config">
          <p className="font-display font-bold text-navy mb-2">Dinner Plans &amp; Prices</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {Object.entries(cfg.packs).map(([k, p]) => (
              <div key={k} className="border border-slate-200 rounded-xl p-2">
                <p className="text-sm font-semibold text-navy">{p.label}</p>
                <p className="text-[10px] text-slate-400">{p.size} credit{p.size === 1 ? "" : "s"}</p>
                <Input type="number" value={p.price} onChange={(e) => setCfg({ ...cfg, packs: { ...cfg.packs, [k]: { ...p, price: parseFloat(e.target.value) || 0 } } })} className="rounded-lg h-8 mt-1" data-testid={`pack-price-${k}`} />
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm"><input type="checkbox" checked={cfg.combo?.active} onChange={(e) => setCfg({ ...cfg, combo: { ...cfg.combo, active: e.target.checked } })} data-testid="combo-toggle" /> Show &ldquo;{cfg.combo?.label}&rdquo; combo promo to parents</label>
          <Button onClick={saveCfg} className="bg-turquoise text-navy rounded-full font-bold mt-3" data-testid="save-dinner-config">Save plans &amp; combo</Button>
        </div>
      )}
      {report && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="dinner-report">
          <p className="font-display font-bold text-navy mb-2">Dinner Reporting</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div className="bg-slate-50 rounded-xl p-2"><p className="font-display font-extrabold text-navy">{money(report.total_sales)}</p><p className="text-[10px] text-slate-400">Meal sales</p></div>
            <div className="bg-slate-50 rounded-xl p-2"><p className="font-display font-extrabold text-navy">{report.orders_count}</p><p className="text-[10px] text-slate-400">Dinners ordered</p></div>
            <div className="bg-slate-50 rounded-xl p-2"><p className="font-display font-extrabold text-navy">{report.unused_credits}</p><p className="text-[10px] text-slate-400">Unused credits</p></div>
            <div className="bg-slate-50 rounded-xl p-2"><p className="font-display font-extrabold text-navy">{report.requests}</p><p className="text-[10px] text-slate-400">Requests</p></div>
          </div>
          {report.meals_by_type.length > 0 && <p className="text-xs text-slate-500 mt-2">Top: {report.meals_by_type.slice(0, 3).map((m) => `${m.meal} (${m.count})`).join(", ")}</p>}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <p className="font-display font-bold text-navy mb-2">Day Menu Builder</p>
        <p className="text-xs text-slate-500 mb-2">Pick a program date, then tick the meals available that day (parents only see these).</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div><Label className="text-xs">Program date</Label><Input type="date" value={menuDate} onChange={(e) => loadMenu(e.target.value)} className="rounded-xl" data-testid="menu-date" /></div>
          {menuDate && <Button onClick={saveMenu} className="bg-turquoise text-navy rounded-full font-bold" data-testid="menu-save">Save day menu</Button>}
        </div>
        {menuDate && (
          <div className="flex flex-wrap gap-2 mt-3">
            {meals.filter((m) => m.active).map((m) => (
              <button key={m.id} onClick={() => toggleMenu(m.id)} data-testid={`menu-toggle-${m.id}`}
                className={`text-sm px-3 py-1.5 rounded-full border ${menuIds.includes(m.id) ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{m.name}</button>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex gap-2 mb-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New meal name" className="rounded-xl" data-testid="new-meal-name" />
          <Button onClick={addMeal} className="bg-turquoise text-navy rounded-full font-bold" data-testid="add-meal-btn"><Icons.Plus className="w-4 h-4 mr-1" /> Add meal</Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {meals.map((m) => <DinnerMealCard key={m.id} m={m} onSaved={load} />)}
        </div>
      </div>

      <div>
        <p className="font-display font-bold text-navy mb-2">Parent Meal Requests ({requests.length})</p>
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-3 text-sm" data-testid={`meal-request-${r.id}`}>
              <p className="font-medium text-navy">{r.parent_name}{r.child_name ? ` · ${r.child_name}` : ""} <span className="text-[10px] font-bold bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">{r.status}</span></p>
              <p className="text-slate-600">{r.suggestion}</p>
              {r.prep_notes && <p className="text-xs text-slate-400">Prep: {r.prep_notes}</p>}
              <div className="flex gap-1 mt-2">
                {["REVIEWING", "APPROVED", "UNABLE TO ACCOMMODATE"].map((st) => (
                  <button key={st} onClick={() => setReqStatus(r.id, st)} data-testid={`req-${st.split(" ")[0].toLowerCase()}-${r.id}`} className={`text-[10px] font-bold rounded-full px-2 py-1 ${r.status === st ? "bg-navy text-white" : "bg-slate-100 text-slate-500"}`}>{st === "UNABLE TO ACCOMMODATE" ? "Unable" : st.charAt(0) + st.slice(1).toLowerCase()}</button>
                ))}
              </div>
            </div>
          ))}
          {requests.length === 0 && <p className="text-slate-400 text-sm">No meal requests yet.</p>}
        </div>
      </div>
    </div>
  );
}

function ProductCard({ p, onSaved }) {
  const [price, setPrice] = useState(p.price);
  const [image, setImage] = useState(p.image || "");
  const [dirty, setDirty] = useState(false);
  const save = async () => {
    try { await api.put(`/admin/products/${p.id}`, { name: p.name, category: p.category, price: parseFloat(price), image }); toast.success("Snack updated"); setDirty(false); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => { await api.delete(`/admin/products/${p.id}`); onSaved(); };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`product-${p.id}`}>
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-xl bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
          {image ? <img src={image} className="w-full h-full object-cover" alt="" /> : <Icons.Cookie className="w-6 h-6 text-slate-300" />}
        </div>
        <div className="flex-1 min-w-0"><p className="font-semibold text-navy truncate">{p.name}</p><p className="text-xs text-slate-400">{p.category}</p></div>
        <Button variant="ghost" size="icon" className="text-coral shrink-0" onClick={del} data-testid={`del-product-${p.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 items-end">
        <div><Label className="text-[10px]">Price ($)</Label><Input type="number" value={price} onChange={(e) => { setPrice(e.target.value); setDirty(true); }} className="rounded-lg h-8" data-testid={`edit-product-price-${p.id}`} /></div>
        <div className="col-span-2"><Label className="text-[10px]">Picture URL</Label><Input value={image} onChange={(e) => { setImage(e.target.value); setDirty(true); }} className="rounded-lg h-8" placeholder="paste picture link" data-testid={`edit-product-image-${p.id}`} /></div>
      </div>
      {dirty && <Button size="sm" onClick={save} className="bg-turquoise text-navy rounded-full font-bold mt-2 w-full" data-testid={`save-product-${p.id}`}>Save changes</Button>}
    </div>
  );
}

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", category: "Snacks", price: "", image: "" });
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/admin/products", { ...form, price: parseFloat(form.price) }); setForm({ name: "", category: "Snacks", price: "", image: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap gap-2 items-end mb-4">
        <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="product-name" /></div>
        <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl" data-testid="product-category" /></div>
        <div><Label className="text-xs">Price</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-xl w-24" data-testid="product-price" /></div>
        <div><Label className="text-xs">Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-xl w-48" data-testid="product-image" placeholder="optional" /></div>
        <Button onClick={add} className="bg-turquoise text-navy rounded-full font-bold" data-testid="add-product-btn"><Icons.Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Tap a snack's price or picture URL to change it, then Save. You can add a picture link anytime.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {products.map((p) => <ProductCard key={p.id} p={p} onSaved={load} />)}
      </div>
    </div>
  );
}

function CreditsPanel() {
  const [credits, setCredits] = useState([]);
  const load = () => api.get("/admin/credits").then((r) => setCredits(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => { await api.post(`/admin/credits/${id}/${action}`); load(); };
  return (
    <div className="mt-6 space-y-3">
      {credits.map((c) => (
        <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between" data-testid={`admin-credit-${c.id}`}>
          <div><p className="font-semibold text-navy">{c.child_name}</p><p className="text-sm text-slate-500">{c.reason} · {c.status}</p></div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(c.id, "extend")} data-testid={`extend-${c.id}`}>Extend</Button>
            <Button size="sm" variant="outline" className="rounded-full text-coral" onClick={() => act(c.id, "remove")} data-testid={`remove-${c.id}`}>Remove</Button>
          </div>
        </div>
      ))}
      {credits.length === 0 && <p className="text-slate-400">No credits issued.</p>}
    </div>
  );
}

const CHILD_SAFETY = [["allergies", "Allergies"], ["dietary", "Dietary restrictions"], ["medical", "Medical info"], ["medications", "Medications"], ["special_support", "Special support"]];
const CHILD_CONTACTS = [["parent_name", "Parent / guardian"], ["parent_phone", "Parent phone"], ["parent_phone_account", "Account phone"], ["parent_email", "Parent email"], ["secondary_guardian", "Secondary guardian"], ["emergency_contact", "Emergency contact"], ["emergency_phone", "Emergency phone"], ["authorized_pickups", "Authorized pickups"]];
const CHILD_FUN = [["favorite_color", "Favourite colour"], ["favorite_animal", "Favourite animal"], ["favorite_food", "Favourite snack / food"], ["favorite_activity", "Favourite thing to do"], ["when_i_grow_up", "When I grow up…"], ["superpower", "My superpower would be…"]];

function ChildProfileModal({ child, onClose, onEdit, onDelete, onLateFee }) {
  if (!child) return null;
  const name = `${child.first_name || ""} ${child.last_name || ""}`.trim() || "Explorer";
  const Row = ([k, l]) => child[k] ? <div key={k} className="flex justify-between gap-3 py-1 border-b border-slate-100 last:border-0"><span className="text-xs text-slate-500 shrink-0">{l}</span><span className="text-sm font-medium text-navy text-right break-words">{child[k]}</span></div> : null;
  const funFilled = CHILD_FUN.filter(([k]) => child[k]);
  return (
    <Dialog open={!!child} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-child-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-turquoise/15 flex items-center justify-center overflow-hidden shrink-0">
              {child.photo_url ? <img src={child.photo_url} className="w-full h-full object-cover" alt="" /> : <Icons.User className="w-7 h-7 text-turquoise" />}
            </div>
            <div className="text-left min-w-0">
              <p className="font-display font-extrabold text-navy truncate">{name}{child.preferred_name ? ` · "${child.preferred_name}"` : ""}</p>
              <p className="font-mono text-turquoise text-xs">{child.explorer_id}</p>
            </div>
            {onEdit && <Button size="sm" variant="outline" className="ml-auto rounded-full" onClick={() => onEdit(child)} data-testid="admin-child-edit-btn"><Icons.Pencil className="w-3.5 h-3.5 mr-1" />Edit profile</Button>}
            {onLateFee && <Button size="sm" variant="outline" className="rounded-full text-coral border-coral/30 hover:bg-coral/5" onClick={() => onLateFee(child)} data-testid="admin-child-latefee-btn"><Icons.Clock className="w-3.5 h-3.5 mr-1" />Late fee</Button>}
            {onDelete && <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => onDelete(child)} data-testid="admin-child-delete-btn"><Icons.Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-2xl p-3" data-testid="admin-child-basic">
            {[["school", "School"], ["grade", "Grade / Year"], ["date_of_birth", "Date of birth"], ["interests", "Preferred activities"]].map(Row)}
          </div>
          <div className="bg-coral/5 border border-coral/20 rounded-2xl p-3" data-testid="admin-child-safety">
            <p className="text-xs font-bold text-coral uppercase tracking-wider mb-1">⚠ Safety &amp; Care</p>
            {CHILD_SAFETY.some(([k]) => child[k]) ? CHILD_SAFETY.map(Row) : <p className="text-sm text-slate-400">No allergies or medical notes on file.</p>}
          </div>
          <div className="bg-slate-50 rounded-2xl p-3" data-testid="admin-child-contacts">
            <p className="text-xs font-bold text-navy uppercase tracking-wider mb-1">Contacts &amp; Pickup</p>
            {CHILD_CONTACTS.map(Row)}
          </div>
          <div className="bg-turquoise/5 border border-turquoise/20 rounded-2xl p-3" data-testid="admin-child-funfacts">
            <p className="text-xs font-bold text-turquoise uppercase tracking-wider mb-1 flex items-center gap-1"><Icons.Heart className="w-3.5 h-3.5" /> Get to know me</p>
            {funFilled.length ? funFilled.map(Row) : <p className="text-sm text-slate-400">The family hasn't added fun facts yet.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EnrollDialog({ parentId, onClose, onDone }) {
  const [ctx, setCtx] = useState(null);
  const [childId, setChildId] = useState("");
  const [mode, setMode] = useState("afterschool"); // afterschool | camp
  const [busy, setBusy] = useState(false);
  const [addChild, setAddChild] = useState(false);
  const [fullProfile, setFullProfile] = useState(false);
  const [nc, setNc] = useState({ first_name: "", last_name: "", date_of_birth: "", school: "" });
  // afterschool state
  const [pkg, setPkg] = useState("daily");
  const [asDates, setAsDates] = useState([]);
  const [asTransport, setAsTransport] = useState("none");
  const [tPick, setTPick] = useState("");
  const [asPass, setAsPass] = useState(false);
  const [tDrop, setTDrop] = useState("");
  const [homework, setHomework] = useState(false);
  const [asLoc, setAsLoc] = useState("berkeley");
  // camp state
  const [campId, setCampId] = useState("");
  const [campOpt, setCampOpt] = useState("");
  const [campDates, setCampDates] = useState([]);
  const [campTransport, setCampTransport] = useState("none");
  const [markPaid, setMarkPaid] = useState(false);
  // dinner state
  const [dinnerDates, setDinnerDates] = useState([]);
  const [dinnerDate, setDinnerDate] = useState("");
  const [dinnerMeals, setDinnerMeals] = useState([]);
  const [dinnerMeal, setDinnerMeal] = useState("");
  const [dinnerSide, setDinnerSide] = useState("");
  const [dinnerNotes, setDinnerNotes] = useState("");

  const loadCtx = async () => {
    if (!parentId) return;
    try { const { data } = await api.get(`/admin/enroll/context/${parentId}`); setCtx(data); setChildId(data.children[0]?.id || ""); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { if (parentId) { setCtx(null); setMode("afterschool"); setAddChild(false); setAsDates([]); setCampDates([]); setCampId(""); setCampOpt(""); setHomework(false); setAsTransport("none"); setCampTransport("none"); setPkg("daily"); setAsLoc("berkeley"); setMarkPaid(false); setDinnerDates([]); setDinnerDate(""); setDinnerMeals([]); setDinnerMeal(""); setDinnerSide(""); setDinnerNotes(""); loadCtx(); } }, [parentId]); // eslint-disable-line

  if (!parentId) return null;
  const child = ctx?.children.find((c) => c.id === childId);
  const camp = ctx?.camps.find((c) => c.id === campId);
  const homeworkAddon = ctx?.addons.find((a) => a.key === "homework_done");
  const childProspect = /prospect/i.test(child?.school || "");
  const inHouse = childProspect && asLoc === "prospect" && pkg !== "transport";
  const asPricing = inHouse ? ctx.prospect_pricing : ctx?.pricing;
  const asDays = (ctx?.program_dates || []).filter((d) => !inHouse || !ctx.prospect_end_date || d.date <= ctx.prospect_end_date);
  const pickLoc = (l) => { setAsLoc(l); setAsDates([]); if (l === "prospect") setAsTransport("none"); };

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const doAddChild = async () => {
    if (!nc.first_name.trim() || !nc.last_name.trim()) return toast.error("Enter the child's first and last name.");
    try {
      const { data } = await api.post("/admin/enroll/child", { ...nc, parent_id: parentId });
      toast.success(`Added ${data.first_name}`);
      setAddChild(false); setNc({ first_name: "", last_name: "", date_of_birth: "", school: "" });
      await loadCtx(); setChildId(data.id); onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submitAfterschool = async () => {
    if (!childId) return toast.error("Pick an Explorer.");
    if (asDates.length === 0) return toast.error("Select at least one day.");
    const passOn = pkg === "transport" && asPass;
    if (passOn && asDates.length !== 5) return toast.error(`Pick exactly 5 days for the Transport Pass (${asDates.length} selected).`);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/enroll/afterschool", {
        parent_id: parentId, child_id: childId, package_type: pkg, dates: asDates,
        addons: homework && homeworkAddon ? ["homework_done"] : [], transport: inHouse ? "none" : asTransport, location: inHouse ? "prospect" : "berkeley",
        transport_info: { ...(child?.school ? { school: child.school } : {}), ...(pkg === "transport" ? { pickup_location: tPick, dropoff_address: tDrop } : {}) }, mark_paid: markPaid, transport_pass: passOn,
      });
      toast.success(`${data.paid ? "Booked & paid" : "Booked"}! ${money(data.amount)} · ref ${data.reference}`);
      setAsDates([]); setHomework(false); setAsTransport("none"); onDone && onDone(); loadCtx();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const submitCamp = async () => {
    if (!childId) return toast.error("Pick an Explorer.");
    if (!campId) return toast.error("Pick a camp.");
    if (!campOpt) return toast.error("Pick a camp option.");
    const need = parseInt(campOpt);
    if (campDates.length !== need) return toast.error(`Select exactly ${need} camp days.`);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/enroll/camp", {
        parent_id: parentId, child_id: childId, camp_id: campId, option_key: `${need}day`,
        dates: campDates, transport: camp?.transport_enabled ? campTransport : "none",
        transport_info: child?.school ? { school: child.school } : {}, mark_paid: markPaid,
      });
      toast.success(`${data.paid ? "Registered & paid" : "Registered"}! ${money(data.amount)} · ref ${data.reference}`);
      setCampDates([]); setCampOpt(""); setCampId(""); onDone && onDone(); loadCtx();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const loadDinnerDates = async (cid) => {
    if (!cid) return;
    try { const { data } = await api.get(`/admin/enroll/dinner-dates/${cid}`); setDinnerDates(data.dates); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const loadDinnerMeals = async (d) => {
    setDinnerDate(d); setDinnerMeal(""); setDinnerSide(""); setDinnerMeals([]);
    try { const { data } = await api.get(`/dinner/meals?date=${d}`); setDinnerMeals(data.meals); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const submitDinner = async () => {
    if (!childId) return toast.error("Pick an Explorer.");
    if (!dinnerDate) return toast.error("Pick a booked day.");
    if (!dinnerMeal) return toast.error("Pick a meal.");
    const m = dinnerMeals.find((x) => x.id === dinnerMeal);
    if (m?.sides?.length && !dinnerSide) return toast.error("Choose a side.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/enroll/dinner", {
        parent_id: parentId, child_id: childId, date: dinnerDate, meal_id: dinnerMeal,
        side: dinnerSide, notes: dinnerNotes, mark_paid: markPaid,
      });
      toast.success(`${data.paid ? "Dinner added & paid" : "Dinner added"}! ${money(data.amount)} · ref ${data.reference}`);
      setDinnerMeal(""); setDinnerSide(""); setDinnerNotes(""); setDinnerDate("");
      loadDinnerDates(childId); onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const remindWaiver = async () => {
    try { const { data } = await api.post(`/admin/children/${childId}/waiver-reminder`); toast.success(data.emailed ? "Waiver reminder sent (in-app + email)" : "Waiver reminder sent"); }
    catch (e) { toast.error(apiErr(e)); }
  };


  const TransportPicker = ({ val, setVal, price }) => (
    <div>
      <Label className="text-xs text-slate-500">Transportation (${price} each way)</Label>
      <div className="flex flex-wrap gap-2 mt-1">
        {[["none", "None"], ["pickup", "Pick-up only"], ["dropoff", "Drop-off only"], ["both", "Both ways"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setVal(k)} data-testid={`enroll-transport-${k}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${val === k ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{l}</button>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={!!parentId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enroll-dialog">
        <DialogHeader>
          <DialogTitle className="text-left">
            <p className="font-display font-extrabold text-navy">Book an adventure for this parent</p>
            {ctx && <p className="text-xs text-slate-400 font-normal">{ctx.parent.name} · {ctx.parent.email}</p>}
          </DialogTitle>
        </DialogHeader>
        {!ctx ? <p className="text-slate-400 text-sm py-6">Loading…</p> : (
          <div className="space-y-4">
            {/* child picker */}
            <div>
              <Label className="text-xs text-slate-500">Explorer</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ctx.children.map((c) => (
                  <button key={c.id} onClick={() => { setChildId(c.id); if (mode === "dinner") loadDinnerDates(c.id); }} data-testid={`enroll-child-${c.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border ${childId === c.id ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-600"}`}>{c.first_name}</button>
                ))}
                <button onClick={() => setAddChild((v) => !v)} data-testid="enroll-add-child-toggle" className="px-3 py-1.5 rounded-full text-sm border border-dashed border-turquoise text-turquoise font-semibold">+ Add Explorer</button>
              </div>
              {addChild && (
                <div className="mt-2 grid grid-cols-2 gap-2 bg-slate-50 rounded-xl p-3" data-testid="enroll-add-child-form">
                  <Input placeholder="First name" value={nc.first_name} onChange={(e) => setNc({ ...nc, first_name: e.target.value })} className="rounded-lg h-9" data-testid="enroll-nc-first" />
                  <Input placeholder="Last name" value={nc.last_name} onChange={(e) => setNc({ ...nc, last_name: e.target.value })} className="rounded-lg h-9" data-testid="enroll-nc-last" />
                  <div><Label className="text-[10px] text-slate-500">Date of birth</Label><Input type="date" value={nc.date_of_birth} onChange={(e) => setNc({ ...nc, date_of_birth: e.target.value })} className="rounded-lg h-9" data-testid="enroll-nc-dob" /></div>
                  <div><Label className="text-[10px] text-slate-500">School</Label><Input list="enroll-school-list" placeholder="School" value={nc.school} onChange={(e) => setNc({ ...nc, school: e.target.value })} className="rounded-lg h-9" data-testid="enroll-nc-school" /><datalist id="enroll-school-list"><option value="Prospect Preschool" /></datalist>
                    <button type="button" onClick={() => setNc({ ...nc, school: "Prospect Preschool" })} className="text-[10px] text-gold font-bold mt-0.5 underline" data-testid="enroll-nc-prospect">Prospect Preschool (in-house)</button></div>
                  <Button onClick={doAddChild} className="col-span-2 bg-turquoise text-navy rounded-full font-bold h-9" data-testid="enroll-nc-save">Add Explorer</Button>
                  <button type="button" onClick={() => { setAddChild(false); setFullProfile(true); }} className="col-span-2 text-xs text-navy underline" data-testid="enroll-full-profile-btn">Add with full profile (contacts, allergies, medical, fun facts)</button>
                </div>
              )}
              {child && !child.waiver_signed && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-2 mt-2" data-testid="enroll-waiver-warning">
                  <Icons.AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-amber-700">Waiver not signed for {child.first_name}. You can still enroll — remind the family to sign it in their dashboard before the first day.</p>
                    <button onClick={remindWaiver} data-testid="enroll-waiver-remind" className="mt-1 text-xs font-bold text-amber-800 underline">Send waiver reminder</button>
                  </div>
                </div>
              )}
            </div>

            {ctx.children.length === 0 ? (
              <p className="text-sm text-slate-400">Add an Explorer above to enroll them.</p>
            ) : (
              <>
                {/* mode tabs */}
                <div className="flex gap-2">
                  <button onClick={() => { setMode("afterschool"); if (pkg === "transport") setPkg("daily"); }} data-testid="enroll-mode-afterschool" className={`flex-1 py-2 rounded-xl text-sm font-semibold border ${mode === "afterschool" && pkg !== "transport" ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-500"}`}>After-school</button>
                  <button onClick={() => setMode("camp")} data-testid="enroll-mode-camp" className={`flex-1 py-2 rounded-xl text-sm font-semibold border ${mode === "camp" ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-500"}`}>Camp</button>
                  <button onClick={() => { setMode("dinner"); loadDinnerDates(childId); }} data-testid="enroll-mode-dinner" className={`flex-1 py-2 rounded-xl text-sm font-semibold border ${mode === "dinner" ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-500"}`}>Dinner</button>
                  <button onClick={() => { setMode("afterschool"); setPkg("transport"); setAsLoc("berkeley"); if (asTransport === "none") setAsTransport("both"); }} data-testid="enroll-mode-transport" className={`flex-1 py-2 rounded-xl text-sm font-semibold border ${mode === "afterschool" && pkg === "transport" ? "bg-gold text-navy border-gold" : "border-slate-200 text-slate-500"}`}>Transport only</button>
                </div>

                <label className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="enroll-mark-paid">
                  <input type="checkbox" checked={markPaid} onChange={(e) => setMarkPaid(e.target.checked)} />
                  Mark as already paid / comped (confirms instantly — no payment owed)
                </label>

                {mode === "afterschool" && (
                  <div className="space-y-3" data-testid="enroll-afterschool">
                    {childProspect && pkg !== "transport" && (
                      <div>
                        <Label className="text-xs text-slate-500">Program location</Label>
                        <div className="flex gap-2 mt-1">
                          {[["berkeley", "Berkeley Center"], ["prospect", "In-house at Prospect Preschool"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => pickLoc(k)} data-testid={`enroll-loc-${k}`} className={`text-xs px-3 py-1.5 rounded-full border ${(inHouse ? "prospect" : "berkeley") === k ? "bg-gold/20 border-gold text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{l}</button>
                          ))}
                        </div>
                        {inHouse && ctx.prospect_end_date && <p className="text-[11px] text-slate-500 mt-1">No transport needed · days after {new Date(ctx.prospect_end_date + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })} not available.</p>}
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-slate-500">Package</Label>
                      <div className="flex gap-2 mt-1">
                        {(pkg === "transport" ? [] : [["daily", `Daily $${asPricing.daily}`], ["weekly", `Weekly $${asPricing.weekly}`], ["monthly", `Monthly $${asPricing.monthly}`]]).map(([k, l]) => (
                          <button key={k} onClick={() => setPkg(k)} data-testid={`enroll-pkg-${k}`} className={`text-xs px-3 py-1.5 rounded-full border ${pkg === k ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Program days ({asDates.length} selected)</Label>
                      {asDays.length === 0 ? <p className="text-sm text-slate-400 mt-1">No open program days.</p> : (
                        <div className="grid grid-cols-3 gap-1.5 mt-1 max-h-44 overflow-y-auto">
                          {asDays.map((d) => (
                            <button key={d.date} onClick={() => toggle(asDates, setAsDates, d.date)} data-testid={`enroll-day-${d.date}`}
                              className={`text-[11px] px-2 py-1.5 rounded-lg border ${asDates.includes(d.date) ? "bg-navy text-white border-navy" : d.past ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-600"}`} title={d.past ? "Past day — booking after the fact" : ""}>
                              {new Date(d.date + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })}{d.past ? " ·past" : ""}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {!inHouse && <TransportPicker val={asTransport} setVal={setAsTransport} price={ctx.transport_price_each_way} />}
                    {pkg === "transport" && (
                      <div className="grid sm:grid-cols-2 gap-2" data-testid="enroll-transport-places">
                        <Input value={tPick} onChange={(e) => setTPick(e.target.value)} placeholder="Pick up from (default: school)" className="rounded-lg h-9" data-testid="enroll-transport-pickup-place" />
                        <Input value={tDrop} onChange={(e) => setTDrop(e.target.value)} placeholder="Drop off at (default: Adventure Rock)" className="rounded-lg h-9" data-testid="enroll-transport-dropoff-place" />
                        <label className="sm:col-span-2 flex items-center gap-2 text-sm text-navy" data-testid="enroll-transport-pass">
                          <input type="checkbox" checked={asPass} onChange={(e) => setAsPass(e.target.checked)} data-testid="enroll-transport-pass-toggle" /> 5-Day Transport Pass — any 5 days, save {ctx.transport_pass_discount_pct ?? 10}%
                        </label>
                        <p className="sm:col-span-2 text-[11px] text-slate-500" data-testid="enroll-transport-total">{asPass ? "Transport Pass" : "Transport only"} · ${ctx.transport_price_each_way} per trip{asPass ? ` − ${ctx.transport_pass_discount_pct ?? 10}%` : ""} · total ${Math.round((asTransport === "both" ? 2 : asTransport === "none" ? 0 : 1) * ctx.transport_price_each_way * asDates.length * (asPass ? 1 - (ctx.transport_pass_discount_pct ?? 10) / 100 : 1) * 100) / 100} for {asDates.length} day(s){asPass && asDates.length !== 5 ? " · pick exactly 5" : ""}</p>
                      </div>
                    )}
                    {homeworkAddon && (
                      <label className="flex items-center gap-2 text-sm" data-testid="enroll-homework">
                        <input type="checkbox" checked={homework} onChange={(e) => setHomework(e.target.checked)} /> {homeworkAddon.name} (+${homeworkAddon.price}/day)
                      </label>
                    )}
                    <Button onClick={submitAfterschool} disabled={busy} className="w-full bg-turquoise text-navy rounded-full font-bold" data-testid="enroll-afterschool-submit">
                      {busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : (markPaid ? `Book ${pkg === "transport" ? "transport" : "after-school"} (mark paid)` : `Book ${pkg === "transport" ? "transport" : "after-school"} (pending payment)`)}
                    </Button>
                  </div>
                )}

                {mode === "camp" && (
                  <div className="space-y-3" data-testid="enroll-camp">
                    <div>
                      <Label className="text-xs text-slate-500">Camp</Label>
                      <div className="flex flex-col gap-1.5 mt-1">
                        {ctx.camps.length === 0 ? <p className="text-sm text-slate-400">No open camps.</p> : ctx.camps.map((c) => (
                          <button key={c.id} onClick={() => { setCampId(c.id); setCampOpt(""); setCampDates([]); }} data-testid={`enroll-camp-${c.id}`}
                            className={`text-left text-sm px-3 py-2 rounded-xl border ${campId === c.id ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-600"}`}>
                            {c.emoji} {c.name} <span className="opacity-70 text-xs">· {c.dates.length} days{c.spots_left != null ? ` · ${c.spots_left} spots` : ""}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    {camp && (<>
                      <div>
                        <Label className="text-xs text-slate-500">Days option</Label>
                        <div className="flex gap-2 mt-1">
                          {(camp.day_options || [3, 5]).map((n) => (
                            <button key={n} onClick={() => { setCampOpt(String(n)); setCampDates([]); }} data-testid={`enroll-campopt-${n}`} className={`text-xs px-3 py-1.5 rounded-full border ${campOpt === String(n) ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{n} days</button>
                          ))}
                        </div>
                      </div>
                      {campOpt && (
                        <div>
                          <Label className="text-xs text-slate-500">Pick {campOpt} days ({campDates.length}/{campOpt})</Label>
                          <div className="grid grid-cols-3 gap-1.5 mt-1 max-h-40 overflow-y-auto">
                            {camp.dates.map((d) => {
                              const active = campDates.includes(d);
                              const disabled = !active && campDates.length >= parseInt(campOpt);
                              return (
                                <button key={d} disabled={disabled} onClick={() => toggle(campDates, setCampDates, d)} data-testid={`enroll-campday-${d}`}
                                  className={`text-[11px] px-2 py-1.5 rounded-lg border ${active ? "bg-navy text-white border-navy" : disabled ? "border-slate-100 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                                  {new Date(d + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {camp.transport_enabled && <TransportPicker val={campTransport} setVal={setCampTransport} price={ctx.transport_price_each_way} />}
                    </>)}
                    <Button onClick={submitCamp} disabled={busy} className="w-full bg-turquoise text-navy rounded-full font-bold" data-testid="enroll-camp-submit">
                      {busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : (markPaid ? "Register for camp (mark paid)" : "Register for camp (pending payment)")}
                    </Button>
                  </div>
                )}

                {mode === "dinner" && (
                  <div className="space-y-3" data-testid="enroll-dinner">
                    <div>
                      <Label className="text-xs text-slate-500">Booked day (future days with a menu)</Label>
                      {dinnerDates.length === 0 ? (
                        <p className="text-sm text-slate-400 mt-1">No upcoming booked days with a dinner menu. Book a day first, and make sure a menu is set for it in Admin → Dinner.</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-1.5 mt-1 max-h-40 overflow-y-auto">
                          {dinnerDates.map((d) => (
                            <button key={d} onClick={() => loadDinnerMeals(d)} data-testid={`enroll-dinner-date-${d}`}
                              className={`text-[11px] px-2 py-1.5 rounded-lg border ${dinnerDate === d ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-600"}`}>
                              {new Date(d + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {dinnerDate && (
                      <div className="space-y-2">
                        {dinnerMeals.length === 0 ? <p className="text-sm text-slate-400">No meals on the menu for this day.</p> : dinnerMeals.map((m) => (
                          <div key={m.id}>
                            <label className={`flex items-center gap-3 rounded-xl border-2 p-3 cursor-pointer ${dinnerMeal === m.id ? "border-turquoise bg-turquoise/5" : "border-slate-200"} ${m.sold_out ? "opacity-50" : ""}`} data-testid={`enroll-dinner-meal-${m.id}`}>
                              <input type="radio" name="enroll-meal" disabled={m.sold_out} checked={dinnerMeal === m.id} onChange={() => { setDinnerMeal(m.id); setDinnerSide(""); }} />
                              <div className="flex-1"><p className="font-semibold text-navy text-sm">{m.name} {m.sold_out && <span className="text-xs text-coral">SOLD OUT</span>}</p><p className="text-xs text-slate-500">{m.description}</p></div>
                              <span className="font-bold text-navy text-sm">{m.price > 0 ? `$${m.price.toFixed(2)}` : "TBD"}</span>
                            </label>
                            {dinnerMeal === m.id && m.sides?.length > 0 && (
                              <div className="ml-8 mt-2" data-testid={`enroll-dinner-sides-${m.id}`}>
                                <Label className="text-xs text-slate-500">Choose a side</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {m.sides.map((sd) => (
                                    <button key={sd} type="button" onClick={() => setDinnerSide(sd)} data-testid={`enroll-dinner-side-${sd.replace(/\s/g, "-").toLowerCase()}`}
                                      className={`text-xs px-3 py-1.5 rounded-full border ${dinnerSide === sd ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{sd}</button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        <Input placeholder="Notes (optional)" value={dinnerNotes} onChange={(e) => setDinnerNotes(e.target.value)} className="rounded-lg h-9 text-sm" data-testid="enroll-dinner-notes" />
                        <Button onClick={submitDinner} disabled={busy} className="w-full bg-turquoise text-navy rounded-full font-bold" data-testid="enroll-dinner-submit">
                          {busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : (markPaid ? "Add dinner (mark paid)" : "Add dinner (pending payment)")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        <ExplorerProfileForm open={fullProfile} parentId={parentId} onClose={() => setFullProfile(false)} onSaved={async (d) => { setFullProfile(false); await loadCtx(); if (d?.id) setChildId(d.id); onDone && onDone(); }} />
      </DialogContent>
    </Dialog>
  );
}


const INTEREST_OPTS = [["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["holiday_camps", "Holiday Camps"], ["birthday_parties", "Birthday Parties"]];

function DeleteParentDialog({ parent, onClose, onDeleted }) {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (parent) setTxt(""); }, [parent]);
  if (!parent) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/parents/${parent.id}`, { data: { confirm: "DELETE" } });
      toast.success(`Deleted ${parent.name}`);
      onClose(); onDeleted && onDeleted();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!parent} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="delete-parent-dialog">
        <DialogHeader><DialogTitle className="text-left font-display font-extrabold text-red-600">Delete parent profile</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">This permanently deletes <b>{parent.name}</b> ({parent.email}) and all their data — Explorers, bookings, orders, dinner orders, credits, wallet and mailing-list entry. This cannot be undone.</p>
          <div><Label className="text-xs text-slate-500">Type DELETE to confirm</Label><Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="DELETE" className="rounded-lg h-9" data-testid="delete-parent-confirm" /></div>
          <Button onClick={submit} disabled={txt !== "DELETE" || busy} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold rounded-full disabled:opacity-40" data-testid="delete-parent-submit">
            {busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : "Delete permanently"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddParentDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", package_interest: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ name: "", email: "", phone: "", password: "", package_interest: [] }); }, [open]);
  const toggleInterest = (k) => setF((s) => ({ ...s, package_interest: s.package_interest.includes(k) ? s.package_interest.filter((x) => x !== k) : [...s.package_interest, k] }));
  const submit = async () => {
    if (!f.name.trim() || !f.email.trim()) return toast.error("Enter the parent's name and email.");
    if (f.password.length < 6) return toast.error("Set a password of at least 6 characters.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/parents", { ...f, email: f.email.trim().toLowerCase() });
      toast.success(`Created account for ${data.name} — now enroll their Explorer`);
      onClose(); onCreated && onCreated(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-parent-dialog">
        <DialogHeader><DialogTitle className="text-left font-display font-extrabold text-navy">Add a parent</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Create an account for a family yourself. Share the email + password with them so they can log in later.</p>
          <div><Label className="text-xs text-slate-500">Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-lg h-9" data-testid="add-parent-name" /></div>
          <div><Label className="text-xs text-slate-500">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-lg h-9" data-testid="add-parent-email" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-lg h-9" data-testid="add-parent-phone" /></div>
            <div><Label className="text-xs text-slate-500">Temp password</Label><Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="rounded-lg h-9" data-testid="add-parent-password" /></div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Interested in</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {INTEREST_OPTS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => toggleInterest(k)} data-testid={`add-parent-interest-${k}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${f.package_interest.includes(k) ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          <Button onClick={submit} disabled={busy} className="w-full bg-turquoise text-navy rounded-full font-bold" data-testid="add-parent-submit">
            {busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : "Create account & enroll"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function PeoplePanel() {
  const [parents, setParents] = useState([]);
  const [children, setChildren] = useState([]);
  const [detail, setDetail] = useState(null);
  const [enrollId, setEnrollId] = useState(null);
  const [addParent, setAddParent] = useState(false);
  const [delParent, setDelParent] = useState(null);
  const [editChild, setEditChild] = useState(null);
  const [visitsKey, setVisitsKey] = useState(0);
  const [newChild, setNewChild] = useState(false);
  const [linkChild, setLinkChild] = useState(null);
  const [parentFor, setParentFor] = useState(null);
  const [invite, setInvite] = useState(null);
  const [lateChild, setLateChild] = useState(null);
  const linkTo = async (child, p) => { try { await api.post(`/admin/children/${child.id}/link-parent`, { parent_id: p.id }); toast.success(`${child.first_name} linked to ${p.name}`); } catch (e) { toast.error(apiErr(e)); } load(); };
  const load = () => { setVisitsKey((k) => k + 1); api.get("/admin/parents").then((r) => setParents(r.data)); api.get("/admin/children").then((r) => setChildren(r.data)); };
  useEffect(() => { load(); }, []);
  const openDetail = async (id) => { try { const { data } = await api.get(`/staff/children/${id}/profile`); setDetail(data); } catch (e) { toast.error(apiErr(e)); } };
  const addToList = async (p) => { try { await api.post(`/admin/parents/${p.id}/add-to-mailing-list`); toast.success(`${p.name} added to the mailing list`); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-6">
      <UnbookedVisitsAlert onBook={setEnrollId} refreshKey={visitsKey} />
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="font-display font-bold text-navy">Parents ({parents.length})</p>
          <Button onClick={() => setAddParent(true)} className="bg-navy text-white rounded-full h-8 px-3 text-xs font-bold" data-testid="admin-add-parent-btn"><Icons.UserPlus className="w-3.5 h-3.5 mr-1" />Add parent</Button>
        </div>
        <p className="text-xs text-slate-500 mb-2">Tap <b>Book adventure</b> on a parent to book after-school (Berkeley or Prospect in-house), camp or dinner for their Explorer, or to add a new Explorer profile.</p>
        <div className="space-y-2">{parents.map((p) => (
          <div key={p.id} onClick={() => setEnrollId(p.id)} className="w-full text-left bg-white rounded-xl border border-slate-200 p-3 flex justify-between items-start gap-2 hover:border-turquoise transition-colors cursor-pointer" data-testid={`admin-parent-${p.id}`}>
            <div className="min-w-0">
              <span className="text-sm text-navy font-medium">{p.name}<span className="text-slate-400"> · {p.email}</span></span>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {(p.package_interest || []).map((k) => <span key={k} data-testid={`parent-interest-${p.id}`} className="text-[10px] font-semibold bg-turquoise/15 text-navy rounded-full px-2 py-0.5 capitalize">{k}</span>)}
                {p.on_mailing_list
                  ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5" data-testid={`parent-onlist-${p.id}`}><Icons.Mail className="w-3 h-3" />On mailing list</span>
                  : <button onClick={(e) => { e.stopPropagation(); addToList(p); }} data-testid={`parent-addlist-${p.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold text-turquoise border border-turquoise rounded-full px-2 py-0.5 hover:bg-turquoise/10"><Icons.MailPlus className="w-3 h-3" />Add to mailing list</button>}
              </div>
            </div>
            <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}><Button size="sm" onClick={() => setEnrollId(p.id)} className="h-7 px-2.5 rounded-full bg-gold text-navy text-[11px] font-bold hover:bg-gold/90" data-testid={`parent-book-${p.id}`}><Icons.CalendarPlus className="w-3.5 h-3.5 mr-1" />Book adventure</Button><Button size="sm" variant="outline" onClick={() => setInvite(p)} className="h-7 px-2.5 rounded-full text-[11px] font-bold" title={p.claimed_at ? "Parent has set their password" : p.invited_at ? "Invite sent — not claimed yet" : "Send account invite"} data-testid={`parent-invite-${p.id}`}><Icons.Send className="w-3.5 h-3.5 mr-1" />{p.claimed_at ? "Claimed" : p.invited_at ? "Re-invite" : "Invite"}</Button><Badge className="bg-slate-100 text-slate-600 border-0">{p.children_count} kids</Badge><button onClick={() => setDelParent(p)} data-testid={`parent-delete-${p.id}`} className="text-slate-300 hover:text-red-600 p-1" title="Delete parent"><Icons.Trash2 className="w-4 h-4" /></button><Icons.ChevronRight className="w-4 h-4 text-slate-300" /></span>
          </div>
        ))}</div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="font-display font-bold text-navy">Explorers ({children.length})</p>
          <Button size="sm" onClick={() => setNewChild(true)} className="rounded-full bg-turquoise text-navy font-bold h-8 hover:bg-turquoise/90" data-testid="admin-add-explorer-btn"><Icons.Plus className="w-4 h-4 mr-1" />Add Explorer</Button>
        </div>
        <p className="text-xs text-slate-500 mb-2">Tap an explorer to see their full profile, safety info &amp; fun facts.</p>
        <div className="space-y-2">{children.map((c) => (
          <div key={c.id} role="button" tabIndex={0} onClick={() => openDetail(c.id)} className={`w-full text-left bg-white rounded-xl border p-3 flex justify-between items-center gap-2 hover:border-turquoise transition-colors cursor-pointer ${c.parent_id ? "border-slate-200" : "border-amber-300"}`} data-testid={`admin-child-${c.id}`}>
            <span className="text-sm text-navy font-medium">{c.first_name} {c.last_name}{c.allergies ? <span className="text-[11px] text-coral ml-2">⚠ {c.allergies}</span> : ""}
              {!c.parent_id && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`no-parent-badge-${c.id}`}>No parent yet</span>}</span>
            <span className="flex items-center gap-1 shrink-0">
              {!c.parent_id && <Button size="sm" onClick={(e) => { e.stopPropagation(); setLinkChild(c); }} className="h-7 px-2.5 rounded-full bg-gold text-navy text-[11px] font-bold hover:bg-gold/90" data-testid={`link-parent-btn-${c.id}`}>Link parent</Button>}
              <span className="font-mono text-xs text-turquoise">{c.explorer_id}</span><Icons.ChevronRight className="w-4 h-4 text-slate-300" /></span>
          </div>
        ))}
        {children.length === 0 && <p className="text-slate-400 text-sm">No explorers registered yet.</p>}
        <RecentlyDeleted refreshKey={visitsKey} onRestored={load} /></div>
      </div>
      <LateFeeDialog child={lateChild} onClose={() => setLateChild(null)} />
      <ChildProfileModal child={detail} onClose={() => setDetail(null)} onLateFee={(c) => { setDetail(null); setLateChild({ id: c.id, name: `${c.first_name} ${c.last_name}` }); }} onEdit={(c) => { setDetail(null); setEditChild(c); }} onDelete={async (c) => {
        const ok = window.prompt(`Permanently delete ${c.first_name} ${c.last_name}? This removes their profile, bookings, unpaid orders, credits and passes (you can restore it from \"Recently deleted\" for 30 days). Paid order records are kept.\n\nType DELETE to confirm:`);
        if (ok !== "DELETE") { if (ok !== null) toast.error("Not deleted — you must type DELETE"); return; }
        try { await api.delete(`/admin/children/${c.id}`, { data: { confirm: "DELETE" } }); toast.success(`${c.first_name} deleted`); setDetail(null); load(); }
        catch (e) { toast.error(apiErr(e)); }
      }} />
      <ExplorerProfileForm open={!!editChild} child={editChild} onClose={() => setEditChild(null)} onSaved={() => { setEditChild(null); load(); }} />
      <EnrollDialog parentId={enrollId} onClose={() => setEnrollId(null)} onDone={load} />
      <AddParentDialog open={addParent || !!parentFor} onClose={() => { setAddParent(false); setParentFor(null); }} onCreated={(p) => { if (parentFor) { linkTo(parentFor, p); setParentFor(null); } else { load(); setEnrollId(p.id); } }} />
      <ExplorerProfileForm open={newChild} onClose={() => setNewChild(false)} onSaved={() => { setNewChild(false); load(); }} />
      <InviteParentDialog parent={invite} onClose={() => { setInvite(null); load(); }} />
      <LinkParentDialog child={linkChild} parents={parents} onClose={() => setLinkChild(null)} onLinked={() => { setLinkChild(null); load(); }} onCreateParent={(c) => { setLinkChild(null); setParentFor(c); }} />
      <DeleteParentDialog parent={delParent} onClose={() => setDelParent(null)} onDeleted={load} />
    </div>
  );
}

function WaitlistPanel() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/waitlist").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const notify = async (id) => { await api.post(`/admin/waitlist/${id}/notify`); toast.success("Parent notified"); load(); };
  return (
    <div className="mt-6 space-y-3">
      {list.map((w) => (
        <div key={w.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between" data-testid={`admin-waitlist-${w.id}`}>
          <div><p className="font-semibold text-navy">{w.date}</p><p className="text-sm text-slate-500">{w.status}</p></div>
          <Button size="sm" className="bg-turquoise text-navy rounded-full" onClick={() => notify(w.id)} data-testid={`notify-${w.id}`}>Notify</Button>
        </div>
      ))}
      {list.length === 0 && <p className="text-slate-400">Waitlist is empty.</p>}
    </div>
  );
}

function MessagesPanel() {
  const [form, setForm] = useState({ title: "", body: "" });
  const send = async () => {
    try { await api.post("/admin/messages", form); toast.success("Announcement posted"); setForm({ title: "", body: "" }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-6 max-w-lg space-y-3 bg-white rounded-2xl border border-slate-200 p-5">
      <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" data-testid="msg-title" /></div>
      <div><Label className="text-xs">Message</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-xl" rows={3} data-testid="msg-body" /></div>
      <Button onClick={send} className="bg-navy rounded-full font-bold" data-testid="post-message-btn">Post Announcement</Button>
    </div>
  );
}

function SchoolRow({ s, parishes, onSaved }) {
  const [parish, setParish] = useState(s.parish || "");
  const [dismissal, setDismissal] = useState(s.dismissal_time || "");
  const [dirty, setDirty] = useState(false);
  const save = async () => { try { await api.put(`/admin/schools/${s.id}`, { parish, dismissal_time: dismissal }); toast.success("School updated"); setDirty(false); onSaved && onSaved(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async () => { await api.delete(`/admin/schools/${s.id}`); onSaved && onSaved(); };
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-slate-100" data-testid={`school-${s.id}`}>
      <span className="text-navy text-sm flex-1 min-w-0 truncate">{s.name}</span>
      <select value={parish} onChange={(e) => { setParish(e.target.value); setDirty(true); }} className="text-xs border border-slate-200 rounded-lg px-1.5 py-1.5 bg-white" data-testid={`school-parish-${s.id}`}>
        <option value="">Parish…</option>
        {parishes.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <Input value={dismissal} onChange={(e) => { setDismissal(e.target.value); setDirty(true); }} placeholder="3:00 PM" className="rounded-lg h-8 w-24 text-xs" data-testid={`school-dismissal-${s.id}`} />
      {dirty && <Button size="sm" onClick={save} className="bg-turquoise text-navy rounded-full text-xs h-8 px-3" data-testid={`school-save-${s.id}`}>Save</Button>}
      <Button variant="ghost" size="icon" className="text-coral h-7 w-7 shrink-0" onClick={del}><Icons.Trash2 className="w-4 h-4" /></Button>
    </div>
  );
}

function TransportPanel() {
  const PARISHES = ["Sandys", "Southampton", "Warwick", "Paget", "Pembroke", "Devonshire", "Smith's", "Hamilton", "St. George's"];
  const [schools, setSchools] = useState([]);
  const [requests, setRequests] = useState([]);
  const [name, setName] = useState("");
  const [parish, setParish] = useState("");
  const [dismissal, setDismissal] = useState("");
  const [t, setT] = useState({ price_each_way: 12, capacity: 20 });
  const load = () => {
    api.get("/admin/schools").then((r) => setSchools(r.data));
    api.get("/admin/school-requests").then((r) => setRequests(r.data));
    api.get("/admin/settings").then((r) => setT(r.data.transport || { price_each_way: 12, capacity: 20 }));
  };
  useEffect(() => { load(); }, []);
  const addSchool = async () => { if (!name) return; await api.post("/admin/schools", { name, parish, dismissal_time: dismissal }); setName(""); setParish(""); setDismissal(""); load(); };
  const saveT = async () => { try { await api.put("/admin/settings", { transport: { ...t, price_each_way: Number(t.price_each_way), capacity: Number(t.capacity), blackout: t.blackout || [] } }); toast.success("Transport settings saved"); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <p className="font-display font-bold text-navy mb-3">Transport Settings</p>
        <div className="flex gap-3">
          <div><Label className="text-xs">Price each way ($)</Label><Input type="number" value={t.price_each_way} onChange={(e) => setT({ ...t, price_each_way: e.target.value })} className="rounded-xl" data-testid="transport-price" /></div>
          <div><Label className="text-xs">Daily capacity (legs)</Label><Input type="number" value={t.capacity} onChange={(e) => setT({ ...t, capacity: e.target.value })} className="rounded-xl" data-testid="transport-capacity" /></div>
        </div>
        <Button onClick={saveT} className="bg-navy rounded-full font-bold mt-3" data-testid="save-transport-btn">Save</Button>
        <p className="font-display font-bold text-navy mt-6 mb-1">Schools</p>
        <p className="text-xs text-slate-500 mb-3">Set each school's parish (route zone) and dismissal time — the driver pickup board groups by parish and sorts earliest dismissal first.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="School name" className="rounded-xl col-span-2 sm:col-span-1" data-testid="school-name" />
          <select value={parish} onChange={(e) => setParish(e.target.value)} className="text-sm border border-slate-200 rounded-xl px-2 bg-white" data-testid="new-school-parish">
            <option value="">Parish…</option>
            {PARISHES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <Input value={dismissal} onChange={(e) => setDismissal(e.target.value)} placeholder="Dismissal e.g. 3:00 PM" className="rounded-xl" data-testid="new-school-dismissal" />
          <Button onClick={addSchool} className="bg-turquoise text-navy rounded-full font-bold" data-testid="add-school-btn"><Icons.Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
        <div className="space-y-0.5 max-h-72 overflow-y-auto">
          {schools.map((s) => <SchoolRow key={s.id} s={s} parishes={PARISHES} onSaved={load} />)}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <p className="font-display font-bold text-navy mb-3">School Requests ("not listed")</p>
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="text-sm border border-slate-200 rounded-xl p-3" data-testid={`school-req-${r.id}`}><p className="font-semibold text-navy">{r.name}</p><p className="text-slate-500 text-xs">{r.address}</p></div>
          ))}
          {requests.length === 0 && <p className="text-slate-400 text-sm">No requests.</p>}
        </div>
      </div>
    </div>
  );
}

const AGE_LABELS = { "2-4": "Age 2–4", "5-7": "Age 5–7", "8-11": "Age 8–11", multiple: "More than one child" };
const INTEREST_LABELS = { afterschool: "After-school program", pickup: "School pickup", dropoff: "Drop-off service", camps: "Holiday camps", weekend: "Weekend activities", birthday: "Birthday experiences", membership: "Adventure Rock membership" };

function MailingListPanel() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [age, setAge] = useState("all");
  const [interest, setInterest] = useState("all");
  const load = () => api.get("/admin/mailing-list").then((r) => setSubs(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (id) => { try { await api.post(`/admin/mailing-list/${id}/toggle`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const delMember = async (id) => { if (!window.confirm("Delete this subscriber permanently?")) return; try { await api.delete(`/admin/mailing-list/${id}`); toast.success("Subscriber deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const delUnsub = async () => { if (!window.confirm("Delete ALL unsubscribed contacts? This cannot be undone.")) return; try { const { data } = await api.post("/admin/mailing-list/delete-unsubscribed"); toast.success(`Deleted ${data.deleted} unsubscribed contact${data.deleted === 1 ? "" : "s"}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const [issuing, setIssuing] = useState(false);
  const [resendId, setResendId] = useState(null);
  const resendCode = async (id) => {
    setResendId(id);
    try {
      const { data } = await api.post(`/admin/mailing-list/${id}/resend-code`);
      toast.success(`Early Bird code sent (${data.code}).`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setResendId(null); }
  };
  const issueMissing = async () => {
    setIssuing(true);
    try {
      const { data } = await api.post("/admin/early-bird/issue-missing");
      toast.success(data.issued ? `Emailed ${data.issued} new Early Bird code(s).` : "Everyone eligible already has a code — nothing to send.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setIssuing(false); }
  };

  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [sendCodes, setSendCodes] = useState(false);
  const AGE_KEY = Object.fromEntries(Object.entries(AGE_LABELS).map(([k, v]) => [v.toLowerCase().replace(/–/g, "-"), k]));
  const INTEREST_KEY = Object.fromEntries(Object.entries(INTEREST_LABELS).map(([k, v]) => [v.toLowerCase(), k]));

  const parseCsv = (text) => {
    const rows = []; let field = "", row = [], inQ = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQ) {
        if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
        else field += c;
      } else if (c === '"') inQ = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(field); rows.push(row); row = []; field = ""; }
      else field += c;
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows.filter((r) => r.some((x) => (x || "").trim() !== ""));
  };

  const csvToSubscribers = (text) => {
    const rows = parseCsv(text);
    if (!rows.length) return [];
    const header = rows[0].map((h) => h.trim().toLowerCase());
    const emailIdx = header.findIndex((h) => h.includes("email") || h.includes("e-mail"));
    if (emailIdx === -1) {
      // no header / bare list: pick any cell that looks like an email
      const out = [];
      rows.forEach((r) => r.forEach((cell) => { const v = (cell || "").trim(); if (v.includes("@") && v.includes(".")) out.push({ email: v }); }));
      return out;
    }
    const col = (names) => header.findIndex((h) => names.some((n) => h.includes(n)));
    const idx = { fn: col(["first"]), ln: col(["last"]), mob: col(["mobile", "phone"]), age: col(["age"]), int: col(["interest"]), src: col(["source"]), camp: col(["campaign"]), med: col(["medium"]), ref: col(["referrer", "referral"]), st: col(["status"]) };
    const get = (r, i) => (i >= 0 && r[i] != null ? String(r[i]).trim() : "");
    return rows.slice(1).map((r) => {
      const email = get(r, emailIdx).toLowerCase();
      if (!email.includes("@")) return null;
      const ageRaw = get(r, idx.age); const age = AGE_KEY[ageRaw.toLowerCase().replace(/–/g, "-")] || ageRaw;
      const interests = get(r, idx.int).split(/[;,]/).map((x) => x.trim()).filter(Boolean).map((x) => INTEREST_KEY[x.toLowerCase()] || x);
      return { first_name: get(r, idx.fn), last_name: get(r, idx.ln), email, mobile: get(r, idx.mob), child_age: age, interests, utm_source: get(r, idx.src), utm_campaign: get(r, idx.camp), utm_medium: get(r, idx.med), referrer: get(r, idx.ref), status: (get(r, idx.st) || "subscribed").toLowerCase() };
    }).filter(Boolean);
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const subscribers = csvToSubscribers(await file.text());
      if (!subscribers.length) { toast.error("No email addresses found in that file."); return; }
      const { data } = await api.post("/admin/mailing-list/import", { subscribers, send_codes: sendCodes });
      toast.success(`Imported ${data.imported} new, updated ${data.updated}${data.codes_issued ? `, emailed ${data.codes_issued} Early Bird codes` : ""}.${data.skipped ? ` ${data.skipped} skipped.` : ""}`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const filtered = subs.filter((s) => {
    if (age !== "all" && s.child_age !== age) return false;
    if (interest !== "all" && !(s.interests || []).includes(interest)) return false;
    const t = q.trim().toLowerCase();
    if (t && !`${s.first_name} ${s.last_name} ${s.email} ${s.mobile}`.toLowerCase().includes(t)) return false;
    return true;
  });

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["First Name", "Last Name", "Email", "Mobile", "Child Age", "Interests", "Source", "Campaign", "Medium", "Facebook Ad ID", "Referrer", "Status", "Signed Up"]];
    filtered.forEach((s) => rows.push([
      s.first_name, s.last_name, s.email, s.mobile, AGE_LABELS[s.child_age] || s.child_age,
      (s.interests || []).map((i) => INTEREST_LABELS[i] || i).join("; "),
      s.utm_source, s.utm_campaign, s.utm_medium, s.fbclid, s.referrer, s.status,
      s.created_at ? new Date(s.created_at).toLocaleString() : "",
    ]));
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "mailing-list.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const subscribed = subs.filter((s) => s.status === "subscribed").length;

  const copyText = async (text, label) => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else { const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); }
      toast.success(label);
    } catch { toast.error("Couldn't copy — please try again."); }
  };
  const copyEmails = () => {
    const emails = filtered.filter((s) => s.status === "subscribed").map((s) => s.email);
    if (!emails.length) return toast.error("No subscribed emails to copy in this view.");
    copyText(emails.join(", "), `Copied ${emails.length} email${emails.length > 1 ? "s" : ""} — paste into Gmail's To/BCC field`);
  };

  return (
    <div className="mt-6" data-testid="admin-mailing-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="bg-white rounded-2xl border border-slate-200 px-4 py-2"><span className="font-display font-extrabold text-xl text-navy">{subscribed}</span><span className="text-xs text-slate-400 ml-2">subscribed</span></div>
        <div className="bg-white rounded-2xl border border-slate-200 px-4 py-2"><span className="font-display font-extrabold text-xl text-navy">{subs.length}</span><span className="text-xs text-slate-400 ml-2">total</span></div>
        <div className="relative flex-1 min-w-[180px]">
          <Icons.Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, mobile" className="rounded-xl pl-9" data-testid="mailing-search" />
        </div>
        <select value={age} onChange={(e) => setAge(e.target.value)} className="rounded-xl border border-slate-200 h-10 px-3 text-sm text-navy bg-white" data-testid="mailing-filter-age">
          <option value="all">All ages</option>
          {Object.entries(AGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={interest} onChange={(e) => setInterest(e.target.value)} className="rounded-xl border border-slate-200 h-10 px-3 text-sm text-navy bg-white" data-testid="mailing-filter-interest">
          <option value="all">All interests</option>
          {Object.entries(INTEREST_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Button onClick={copyEmails} disabled={filtered.filter((s) => s.status === "subscribed").length === 0} variant="outline" className="rounded-full font-bold border-turquoise text-navy" data-testid="mailing-copy-emails"><Icons.Copy className="w-4 h-4 mr-1" /> Copy emails ({filtered.filter((s) => s.status === "subscribed").length})</Button>
        <Button onClick={delUnsub} variant="outline" className="rounded-full font-bold border-red-300 text-red-600" data-testid="mailing-delete-unsub"><Icons.Trash2 className="w-4 h-4 mr-1" /> Delete unsubscribed</Button>
        <Button onClick={exportCsv} disabled={filtered.length === 0} className="bg-turquoise text-navy rounded-full font-bold" data-testid="mailing-export"><Icons.Download className="w-4 h-4 mr-1" /> Export CSV</Button>
        {isOwner && (<>
        <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onImportFile} data-testid="mailing-import-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={importing} variant="outline" className="rounded-full font-bold border-turquoise text-navy" data-testid="mailing-import">
          {importing ? <Icons.Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Icons.Upload className="w-4 h-4 mr-1" />} Import CSV
        </Button>
        <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer" data-testid="mailing-sendcodes-label">
          <input type="checkbox" checked={sendCodes} onChange={(e) => setSendCodes(e.target.checked)} className="w-4 h-4 accent-turquoise" data-testid="mailing-sendcodes" />
          Email Early Bird codes on import
        </label>
        <Button onClick={issueMissing} disabled={issuing} variant="outline" className="rounded-full font-bold border-gold text-navy" data-testid="mailing-issue-missing">
          {issuing ? <Icons.Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Icons.MailCheck className="w-4 h-4 mr-1" />} Send missing Early Bird codes
        </Button>
        </>)}
      </div>

      <div className="space-y-2">
        {filtered.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-start justify-between gap-3" data-testid={`mailing-sub-${s.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-navy">{s.first_name} {s.last_name} <span className="text-slate-400 font-normal">· {s.email}</span>
                <button onClick={() => copyText(s.email, "Email copied")} className="ml-2 text-slate-400 hover:text-turquoise align-middle" title="Copy email" data-testid={`mailing-copy-${s.id}`}><Icons.Copy className="w-3.5 h-3.5 inline" /></button>
              </p>
              <p className="text-sm text-slate-500" data-testid={`mailing-mobile-${s.id}`}>
                {s.mobile ? <a href={`tel:${s.mobile.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1 text-navy font-semibold hover:text-turquoise"><Icons.Phone className="w-3.5 h-3.5" />{s.mobile}</a> : <span className="text-slate-400">no mobile</span>}
                {s.mobile && <button onClick={() => copyText(s.mobile, "Number copied")} className="ml-1 text-slate-400 hover:text-turquoise align-middle" title="Copy number" data-testid={`mailing-copy-mobile-${s.id}`}><Icons.Copy className="w-3 h-3 inline" /></button>}
                {s.mobile_from_account && <span className="ml-1 text-[10px] text-slate-400">(from account)</span>}
                {s.child_age ? ` · ${AGE_LABELS[s.child_age] || s.child_age}` : ""}</p>
              {(s.interests || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {s.interests.map((i) => <span key={i} className="text-[10px] bg-navy/5 text-navy rounded-full px-2 py-0.5">{INTEREST_LABELS[i] || i}</span>)}
                </div>
              )}
              <p className="text-[11px] text-slate-400 mt-1">
                {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""} · source: {s.utm_source || s.referrer || "direct"}{s.utm_campaign ? ` / ${s.utm_campaign}` : ""}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Badge className={`border-0 ${s.status === "subscribed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.status}</Badge>
              <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => toggle(s.id)} data-testid={`mailing-toggle-${s.id}`}>{s.status === "subscribed" ? "Unsubscribe" : "Resubscribe"}</Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs border-gold text-navy" disabled={resendId === s.id} onClick={() => resendCode(s.id)} data-testid={`mailing-resend-${s.id}`}>{resendId === s.id ? <Icons.Loader2 className="w-3 h-3 animate-spin" /> : "Resend code"}</Button>
              <Button size="sm" variant="ghost" className="rounded-full text-xs text-slate-400 hover:text-red-600" onClick={() => delMember(s.id)} data-testid={`mailing-delete-${s.id}`}><Icons.Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-slate-400">No subscribers match.</p>}
      </div>
    </div>
  );
}

function LunchMealCard({ m, onSaved }) {
  const [f, setF] = useState({ name: m.name, description: m.description || "", price: m.price || 0, allergens: m.allergens || "", max_qty: m.max_qty ?? "", active: m.active, sold_out: !!m.sold_out, sides: (m.sides || []).join(", ") });
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setDirty(true); };
  const save = async () => {
    try { await api.put(`/admin/lunch/meals/${m.id}`, { ...f, price: parseFloat(f.price) || 0, max_qty: f.max_qty === "" ? null : parseInt(f.max_qty), portions: m.portions || [], sides: f.sides.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Meal saved"); setDirty(false); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => { await api.delete(`/admin/lunch/meals/${m.id}`); onSaved(); };
  return (
    <div className={`bg-white rounded-2xl border p-4 ${f.sold_out ? "border-coral/40" : "border-slate-200"}`} data-testid={`lunch-meal-${m.id}`}>
      <div className="flex items-center gap-2">
        <Input value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-lg h-8 font-semibold" data-testid={`lunch-meal-name-${m.id}`} />
        <Button variant="ghost" size="icon" className="text-coral shrink-0" onClick={del} data-testid={`lunch-meal-del-${m.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
      </div>
      <Input value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" className="rounded-lg h-8 mt-2 text-sm" data-testid={`lunch-meal-desc-${m.id}`} />
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div><Label className="text-[10px]">Price ($)</Label><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="rounded-lg h-8" data-testid={`lunch-meal-price-${m.id}`} /></div>
        <div><Label className="text-[10px]">Max qty/day</Label><Input type="number" value={f.max_qty} onChange={(e) => set("max_qty", e.target.value)} placeholder="∞" className="rounded-lg h-8" data-testid={`lunch-meal-qty-${m.id}`} /></div>
      </div>
      <Input value={f.allergens} onChange={(e) => set("allergens", e.target.value)} placeholder="Allergen notes" className="rounded-lg h-8 mt-2 text-sm" data-testid={`lunch-meal-allergens-${m.id}`} />
      <div className="mt-2"><Label className="text-[10px]">Sides (comma-separated — parents pick one)</Label><Input value={f.sides} onChange={(e) => set("sides", e.target.value)} placeholder="e.g. Fruit, Chips, Veggies" className="rounded-lg h-8 text-sm" data-testid={`lunch-meal-sides-${m.id}`} /></div>
      <div className="flex gap-4 mt-2 text-sm">
        <label className="flex items-center gap-1"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid={`lunch-meal-active-${m.id}`} /> Active</label>
        <label className="flex items-center gap-1"><input type="checkbox" checked={f.sold_out} onChange={(e) => set("sold_out", e.target.checked)} data-testid={`lunch-meal-soldout-${m.id}`} /> Sold out</label>
      </div>
      {dirty && <Button size="sm" onClick={save} className="bg-turquoise text-navy rounded-full font-bold mt-2 w-full" data-testid={`lunch-meal-save-${m.id}`}>Save</Button>}
    </div>
  );
}

function LunchPanel() {
  const [meals, setMeals] = useState([]);
  const [requests, setRequests] = useState([]);
  const [name, setName] = useState("");
  const [menuDate, setMenuDate] = useState("");
  const [menuIds, setMenuIds] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [report, setReport] = useState(null);
  const load = () => {
    api.get("/admin/lunch/meals").then((r) => setMeals(r.data));
    api.get("/admin/lunch/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/admin/lunch/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/lunch/report").then((r) => setReport(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const saveCfg = async () => { try { await api.put("/admin/lunch/config", { packs: cfg.packs }); toast.success("Lunch plans saved"); } catch (e) { toast.error(apiErr(e)); } };
  const setReqStatus = async (id, status) => { try { await api.put(`/admin/lunch/requests/${id}`, { status }); toast.success("Request updated"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const addMeal = async () => { if (!name) return; await api.post("/admin/lunch/meals", { name, price: 0 }); setName(""); load(); };
  const loadMenu = async (d) => { setMenuDate(d); if (!d) return; try { const { data } = await api.get(`/admin/lunch/menu?date=${d}`); setMenuIds(data.meal_ids || []); } catch (e) { toast.error(apiErr(e)); } };
  const toggleMenu = (id) => setMenuIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const saveMenu = async () => { try { await api.put("/admin/lunch/menu", { date: menuDate, meal_ids: menuIds }); toast.success("Day menu saved"); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="mt-6 space-y-6">
      <div className="bg-navy text-white rounded-2xl p-4">
        <p className="font-display font-extrabold">CAMP LUNCH CLUB</p>
        <p className="text-white/60 text-xs">FUEL THE ADVENTURE. — set meals, prices &amp; which options run each camp day (served ~12:00 PM).</p>
      </div>

      {cfg && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="lunch-packs-config">
          <p className="font-display font-bold text-navy mb-2">Lunch Plans &amp; Prices</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {Object.entries(cfg.packs).map(([k, p]) => (
              <div key={k} className="border border-slate-200 rounded-xl p-2">
                <p className="text-sm font-semibold text-navy">{p.label}</p>
                <p className="text-[10px] text-slate-400">{p.size} credit{p.size === 1 ? "" : "s"}</p>
                <Input type="number" value={p.price} onChange={(e) => setCfg({ ...cfg, packs: { ...cfg.packs, [k]: { ...p, price: parseFloat(e.target.value) || 0 } } })} className="rounded-lg h-8 mt-1" data-testid={`lunch-pack-price-${k}`} />
              </div>
            ))}
          </div>
          <Button onClick={saveCfg} className="bg-turquoise text-navy rounded-full font-bold mt-3" data-testid="save-lunch-config">Save plans</Button>
        </div>
      )}
      {report && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="lunch-report">
          <p className="font-display font-bold text-navy mb-2">Lunch Reporting</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div className="bg-slate-50 rounded-xl p-2"><p className="font-display font-extrabold text-navy">{money(report.total_sales)}</p><p className="text-[10px] text-slate-400">Meal sales</p></div>
            <div className="bg-slate-50 rounded-xl p-2"><p className="font-display font-extrabold text-navy">{report.orders_count}</p><p className="text-[10px] text-slate-400">Lunches ordered</p></div>
            <div className="bg-slate-50 rounded-xl p-2"><p className="font-display font-extrabold text-navy">{report.unused_credits}</p><p className="text-[10px] text-slate-400">Unused credits</p></div>
            <div className="bg-slate-50 rounded-xl p-2"><p className="font-display font-extrabold text-navy">{report.requests}</p><p className="text-[10px] text-slate-400">Requests</p></div>
          </div>
          {report.meals_by_type.length > 0 && <p className="text-xs text-slate-500 mt-2">Top: {report.meals_by_type.slice(0, 3).map((m) => `${m.meal} (${m.count})`).join(", ")}</p>}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <p className="font-display font-bold text-navy mb-2">Day Menu Builder</p>
        <p className="text-xs text-slate-500 mb-2">Pick a camp date, then tick the meals available that day (parents only see these).</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div><Label className="text-xs">Camp date</Label><Input type="date" value={menuDate} onChange={(e) => loadMenu(e.target.value)} className="rounded-xl" data-testid="lunch-menu-date" /></div>
          {menuDate && <Button onClick={saveMenu} className="bg-turquoise text-navy rounded-full font-bold" data-testid="lunch-menu-save">Save day menu</Button>}
        </div>
        {menuDate && (
          <div className="flex flex-wrap gap-2 mt-3">
            {meals.filter((m) => m.active).map((m) => (
              <button key={m.id} onClick={() => toggleMenu(m.id)} data-testid={`lunch-menu-toggle-${m.id}`}
                className={`text-sm px-3 py-1.5 rounded-full border ${menuIds.includes(m.id) ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{m.name}</button>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex gap-2 mb-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New meal name" className="rounded-xl" data-testid="new-lunch-meal-name" />
          <Button onClick={addMeal} className="bg-turquoise text-navy rounded-full font-bold" data-testid="add-lunch-meal-btn"><Icons.Plus className="w-4 h-4 mr-1" /> Add meal</Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {meals.map((m) => <LunchMealCard key={m.id} m={m} onSaved={load} />)}
        </div>
      </div>

      <div>
        <p className="font-display font-bold text-navy mb-2">Parent Lunch Requests ({requests.length})</p>
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-3 text-sm" data-testid={`lunch-request-${r.id}`}>
              <p className="font-medium text-navy">{r.parent_name}{r.child_name ? ` · ${r.child_name}` : ""} <span className="text-[10px] font-bold bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">{r.status}</span></p>
              <p className="text-slate-600">{r.suggestion}</p>
              {r.prep_notes && <p className="text-xs text-slate-400">Prep: {r.prep_notes}</p>}
              <div className="flex gap-1 mt-2">
                {["REVIEWING", "APPROVED", "UNABLE TO ACCOMMODATE"].map((st) => (
                  <button key={st} onClick={() => setReqStatus(r.id, st)} data-testid={`lunch-req-${st.split(" ")[0].toLowerCase()}-${r.id}`} className={`text-[10px] font-bold rounded-full px-2 py-1 ${r.status === st ? "bg-navy text-white" : "bg-slate-100 text-slate-500"}`}>{st === "UNABLE TO ACCOMMODATE" ? "Unable" : st.charAt(0) + st.slice(1).toLowerCase()}</button>
                ))}
              </div>
            </div>
          ))}
          {requests.length === 0 && <p className="text-slate-400 text-sm">No lunch requests yet.</p>}
        </div>
      </div>
    </div>
  );
}

function RegistrationsPanel() {
  const [q, setQ] = useState("");
  const [program, setProgram] = useState("all");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!q.trim()) { setResults([]); setSearched(false); return; }
    setBusy(true);
    try { const { data } = await api.get(`/admin/registrations/search?q=${encodeURIComponent(q.trim())}&program=${program}`); setResults(data.results); setSearched(true); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const fmt = (d) => new Date(d + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" });
  return (
    <div className="mt-6 space-y-4">
      <div className="bg-navy text-white rounded-2xl p-4">
        <p className="font-display font-extrabold">FIND A REGISTRATION</p>
        <p className="text-white/60 text-xs">Search who registered for camps or after-school by child name or school.</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]"><Label className="text-xs">Name or school</Label><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. Noah, or Somerset Primary" className="rounded-xl" data-testid="reg-search-input" /></div>
        <div><Label className="text-xs">Program</Label>
          <div className="flex gap-1 mt-1">
            {[["all", "All"], ["afterschool", "After-school"], ["camp", "Camps"]].map(([k, l]) => (
              <button key={k} onClick={() => setProgram(k)} data-testid={`reg-program-${k}`} className={`text-xs font-semibold rounded-full px-3 py-2 border ${program === k ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-500"}`}>{l}</button>
            ))}
          </div>
        </div>
        <Button onClick={run} disabled={busy} className="bg-turquoise text-navy rounded-full font-bold h-10" data-testid="reg-search-btn">{busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <><Icons.Search className="w-4 h-4 mr-1" /> Search</>}</Button>
      </div>
      <div className="space-y-2">
        {searched && results.length === 0 && <p className="text-slate-400 text-sm" data-testid="reg-no-results">No registrations found for “{q}”.</p>}
        {results.map((r) => (
          <div key={r.child_id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`reg-result-${r.child_id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <p className="font-display font-bold text-navy">{r.child_name} <span className="font-mono text-turquoise text-xs">{r.explorer_id}</span></p>
                <p className="text-xs text-slate-500">School: {r.school} · Parent: {r.parent_name}{r.parent_phone ? ` · ${r.parent_phone}` : ""}</p>
              </div>
            </div>
            {r.afterschool_days.length > 0 && (
              <p className="text-sm text-navy mt-2"><b>After-school</b> ({r.afterschool_days.length} day{r.afterschool_days.length === 1 ? "" : "s"}): <span className="text-slate-500">{r.afterschool_days.slice(0, 8).map(fmt).join(", ")}{r.afterschool_days.length > 8 ? "…" : ""}</span></p>
            )}
            {r.camps.map((c) => (
              <p key={c.name} className="text-sm text-navy mt-1"><b>{c.name}</b>: <span className="text-slate-500">{c.dates.map(fmt).join(", ")}</span></p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function PartiesPanel() {
  const [list, setList] = useState([]);
  const [bookings, setBookings] = useState([]);
  const load = () => { api.get("/admin/parties").then((r) => setList(r.data)); api.get("/admin/party/bookings").then((r) => setBookings(r.data)); };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { try { await api.post(`/admin/party/bookings/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="mt-6 space-y-6">
      <div>
        <p className="font-display font-bold text-navy mb-2">Party Bookings ({bookings.length})</p>
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`party-booking-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-navy">{b.parent_name} · {b.date} · {b.guests} children</p>
                  <p className="text-sm text-slate-500">{b.email} · {b.phone}{b.child_name ? ` · for ${b.child_name}` : ""}</p>
                  <p className="text-sm text-navy font-bold mt-1">${Number(b.total).toFixed(0)} total <span className="font-mono text-turquoise text-xs">{b.reference}</span></p>
                  <p className="text-xs text-slate-500">
                    {b.pay_type === "full" ? "Paying in full" : `Deposit $${Number(b.deposit || 0).toFixed(0)}`}
                    {Number(b.balance || 0) > 0 ? ` · balance $${Number(b.balance).toFixed(0)}${b.balance_due ? ` due by ${b.balance_due}` : ""}${b.balance_paid ? " (paid)" : ""}` : ""}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{(b.addons_detail || []).map((a) => `${a.label}${a.price ? ` ($${a.price})` : " (quote)"}`).join(" · ")}</p>
                  {b.notes && <p className="text-xs text-slate-600 mt-1 italic">{b.notes}</p>}
                </div>
                <Badge className={`border-0 shrink-0 ${b.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : b.status === "cancelled" ? "bg-red-100 text-red-700" : b.status === "deposit_held" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{(b.status || "").replace("_", " ")}</Badge>
              </div>
              {b.status !== "confirmed" && b.status !== "cancelled" && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {b.status === "pending" && b.pay_type === "deposit" && Number(b.balance || 0) > 0 && (
                    <Button size="sm" className="bg-sky-500 text-white font-bold rounded-full" onClick={() => setStatus(b.id, "deposit_held")} data-testid={`party-deposit-${b.id}`}>Deposit received (hold date)</Button>
                  )}
                  <Button size="sm" className="bg-turquoise text-navy font-bold rounded-full" onClick={() => setStatus(b.id, "confirmed")} data-testid={`party-confirm-${b.id}`}>{b.status === "deposit_held" ? "Mark fully paid" : "Confirm (paid)"}</Button>
                  <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-600" onClick={() => setStatus(b.id, "cancelled")} data-testid={`party-cancel-${b.id}`}>Cancel</Button>
                </div>
              )}
            </div>
          ))}
          {bookings.length === 0 && <p className="text-slate-400">No party bookings yet.</p>}
        </div>
      </div>
      <div>
        <p className="font-display font-bold text-navy mb-2">Party & School Enquiries</p>
        <div className="space-y-3">
          {list.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`party-${p.id}`}>
              <p className="font-semibold text-navy">{p.name} · {p.date}</p>
              <p className="text-sm text-slate-500">{p.email} · {p.phone} · {p.guests} guests · age {p.child_age}</p>
              {p.message && <p className="text-sm text-slate-600 mt-1">{p.message}</p>}
            </div>
          ))}
          {list.length === 0 && <p className="text-slate-400">No enquiries yet.</p>}
        </div>
      </div>
    </div>
  );
}
